import { z } from 'zod';
/* ---------- Название площадки ---------- */
/*
 * Единственное место, где задано имя, которое видит человек: шапка, меню,
 * приветствие, подпись в PDF. Смена названия — правка этих двух строк и
 * пересборка `shared`.
 *
 * `APP_SLUG` — латиницей, для имён скачиваемых файлов (кириллица в
 * Content-Disposition ломает часть клиентов).
 *
 * Сюда намеренно не входят служебные идентификаторы с тем же корнем:
 * ключи localStorage (`gruzbot_token` и др. — смена разлогинит всех),
 * имя базы и пользователя Postgres, `/opt/gruzbot`, systemd-юниты
 * `gruzbot-*` на сервере. Их никто не видит, а переименование требует
 * ручной работы на боевой машине.
 */
export const APP_NAME = 'GruzBot';
export const APP_SLUG = 'gruzbot';
/* ---------- Ошибки валидации по-русски ---------- */
/*
 * Своё сообщение есть далеко не у каждого поля — их в схемах под сотню, и
 * дописывать текст к каждому значит однажды забыть. Поэтому русский текст
 * ставится один раз здесь, а `message` в конкретной схеме остаётся для тех
 * случаев, где важна формулировка по существу («Опишите причину»), а не
 * «обязательное поле».
 *
 * Карта общая для сервера и интерфейса: `shared` импортируют оба, и
 * несовпадение текста между формой и ответом сервера исключено по
 * построению.
 */
const RU_TYPES = {
    string: 'текст',
    number: 'число',
    integer: 'целое число',
    boolean: 'да/нет',
    date: 'дата',
    array: 'список',
    object: 'объект',
};
z.setErrorMap((issue, ctx) => {
    switch (issue.code) {
        case z.ZodIssueCode.invalid_type:
            if (issue.received === 'undefined' || issue.received === 'null') {
                return { message: 'Обязательное поле' };
            }
            return { message: `Ожидается ${RU_TYPES[issue.expected] ?? issue.expected}` };
        case z.ZodIssueCode.invalid_date:
            return { message: 'Некорректная дата' };
        case z.ZodIssueCode.invalid_enum_value:
            return { message: 'Недопустимое значение' };
        case z.ZodIssueCode.invalid_string:
            if (issue.validation === 'email')
                return { message: 'Некорректная почта' };
            if (issue.validation === 'url')
                return { message: 'Некорректная ссылка' };
            return { message: 'Некорректное значение' };
        case z.ZodIssueCode.too_small:
            if (issue.type === 'string') {
                return issue.minimum === 1
                    ? { message: 'Обязательное поле' }
                    : { message: `Не короче ${issue.minimum} символов` };
            }
            if (issue.type === 'array') {
                return { message: `Выберите хотя бы ${issue.minimum}` };
            }
            return {
                message: issue.inclusive
                    ? `Не меньше ${issue.minimum}`
                    : `Должно быть больше ${issue.minimum}`,
            };
        case z.ZodIssueCode.too_big:
            if (issue.type === 'string')
                return { message: `Не длиннее ${issue.maximum} символов` };
            if (issue.type === 'array')
                return { message: `Не больше ${issue.maximum}` };
            return {
                message: issue.inclusive
                    ? `Не больше ${issue.maximum}`
                    : `Должно быть меньше ${issue.maximum}`,
            };
        case z.ZodIssueCode.not_multiple_of:
            return { message: 'Дробное значение недопустимо' };
        default:
            return { message: ctx.defaultError };
    }
});
/* ---------- Справочники ---------- */
export const BODY_TYPES = [
    // Универсальные
    'TENT', 'CURTAIN_SIDE', 'FLATBED', 'VAN', 'MINIVAN', 'MEGA', 'JUMBO',
    'SWAP_BODY', 'OPEN_TOP',
    // Температурные
    'REF', 'ISOTHERM',
    // Контейнерные
    'CONTAINER', 'CONTAINER_CHASSIS',
    // Сыпучие и самосвальные
    'TIPPER', 'TIPPER_SEMI', 'HOOK_LIFT', 'REFUSE',
    // Наливные
    'TANKER', 'FUEL_TANKER', 'GAS_TANKER', 'FOOD_TANKER', 'BITUMEN', 'VACUUM',
    // Пищевые и сельхоз
    'GRAIN_TRUCK', 'CEMENT_TRUCK', 'FLOUR_TRUCK', 'FEED_TRUCK', 'LIVESTOCK',
    // Негабарит и техника
    'TRAWL', 'LOW_LOADER', 'HEAVY_HAUL', 'CAR_CARRIER',
    // С погрузкой на борту
    'MANIPULATOR', 'CRANE', 'AERIAL_PLATFORM', 'CONCRETE_MIXER', 'CONCRETE_PUMP',
    // Длинномеры и строительные
    'TIMBER', 'PIPE_CARRIER', 'PANEL_CARRIER', 'BLOCK_CARRIER', 'GLASS_CARRIER',
];
export const BODY_TYPE_LABEL = {
    TENT: 'Тент',
    CURTAIN_SIDE: 'Шторный',
    FLATBED: 'Бортовой',
    VAN: 'Фургон',
    MINIVAN: 'Малотоннажник',
    MEGA: 'Мега',
    JUMBO: 'Джамбо',
    SWAP_BODY: 'Съёмный кузов',
    OPEN_TOP: 'Открытый верх',
    REF: 'Рефрижератор',
    ISOTHERM: 'Изотерм',
    CONTAINER: 'Контейнеровоз',
    CONTAINER_CHASSIS: 'Контейнерное шасси',
    TIPPER: 'Самосвал',
    TIPPER_SEMI: 'Самосвальный полуприцеп',
    HOOK_LIFT: 'Мультилифт',
    REFUSE: 'Мусоровоз',
    TANKER: 'Цистерна',
    FUEL_TANKER: 'Бензовоз',
    GAS_TANKER: 'Газовоз',
    FOOD_TANKER: 'Пищевая цистерна',
    BITUMEN: 'Битумовоз',
    VACUUM: 'Илосос',
    GRAIN_TRUCK: 'Зерновоз',
    CEMENT_TRUCK: 'Цементовоз',
    FLOUR_TRUCK: 'Муковоз',
    FEED_TRUCK: 'Кормовоз',
    LIVESTOCK: 'Скотовоз',
    TRAWL: 'Трал',
    LOW_LOADER: 'Низкорамник',
    HEAVY_HAUL: 'Тяжеловоз',
    CAR_CARRIER: 'Автовоз',
    MANIPULATOR: 'Манипулятор',
    CRANE: 'Автокран',
    AERIAL_PLATFORM: 'Автовышка',
    CONCRETE_MIXER: 'Бетоносмеситель',
    CONCRETE_PUMP: 'Бетононасос',
    TIMBER: 'Лесовоз',
    PIPE_CARRIER: 'Трубовоз',
    PANEL_CARRIER: 'Панелевоз',
    BLOCK_CARRIER: 'Блоковоз',
    GLASS_CARRIER: 'Стекловоз',
};
/** Группы для выпадающего списка: сорок позиций подряд не выбрать. */
export const BODY_TYPE_GROUPS = [
    { title: 'Универсальные', types: ['TENT', 'CURTAIN_SIDE', 'FLATBED', 'VAN', 'MINIVAN', 'MEGA', 'JUMBO', 'SWAP_BODY', 'OPEN_TOP'] },
    { title: 'Температурные', types: ['REF', 'ISOTHERM'] },
    { title: 'Контейнерные', types: ['CONTAINER', 'CONTAINER_CHASSIS'] },
    { title: 'Сыпучие и самосвальные', types: ['TIPPER', 'TIPPER_SEMI', 'HOOK_LIFT', 'REFUSE'] },
    { title: 'Наливные', types: ['TANKER', 'FUEL_TANKER', 'GAS_TANKER', 'FOOD_TANKER', 'BITUMEN', 'VACUUM'] },
    { title: 'Пищевые и сельхоз', types: ['GRAIN_TRUCK', 'CEMENT_TRUCK', 'FLOUR_TRUCK', 'FEED_TRUCK', 'LIVESTOCK'] },
    { title: 'Негабарит и техника', types: ['TRAWL', 'LOW_LOADER', 'HEAVY_HAUL', 'CAR_CARRIER'] },
    { title: 'С погрузкой на борту', types: ['MANIPULATOR', 'CRANE', 'AERIAL_PLATFORM', 'CONCRETE_MIXER', 'CONCRETE_PUMP'] },
    { title: 'Длинномеры и строительные', types: ['TIMBER', 'PIPE_CARRIER', 'PANEL_CARRIER', 'BLOCK_CARRIER', 'GLASS_CARRIER'] },
];
/** Кузова со своим краном: для них спрашиваем вылет и грузоподъёмность стрелы. */
export const BODY_TYPES_WITH_CRANE = ['MANIPULATOR', 'CRANE', 'CONCRETE_PUMP', 'AERIAL_PLATFORM'];
export const PAYMENT_TYPES = ['CASH', 'BANK', 'CARD'];
export const PAYMENT_TYPE_LABEL = {
    CASH: 'Наличные',
    BANK: 'Безнал',
    CARD: 'На карту',
};
export const LOADING_TYPES = ['TOP', 'SIDE', 'REAR', 'FULL'];
export const LOADING_TYPE_LABEL = {
    TOP: 'Верхняя',
    SIDE: 'Боковая',
    REAR: 'Задняя',
    FULL: 'Полная растентовка',
};
export const ORDER_STATUSES = [
    'NEW',
    'LOADING',
    'IN_TRANSIT',
    'UNLOADING',
    'DONE',
    'CANCELLED',
];
export const ORDER_STATUS_LABEL = {
    NEW: 'Подтверждён',
    LOADING: 'На погрузке',
    IN_TRANSIT: 'В пути',
    UNLOADING: 'На выгрузке',
    DONE: 'Завершён',
    CANCELLED: 'Отменён',
};
/** Разрешённые переходы статусов рейса. Ключ — текущий статус. */
export const ORDER_STATUS_FLOW = {
    NEW: ['LOADING', 'CANCELLED'],
    LOADING: ['IN_TRANSIT', 'CANCELLED'],
    IN_TRANSIT: ['UNLOADING', 'CANCELLED'],
    UNLOADING: ['DONE'],
    DONE: [],
    CANCELLED: [],
};
/** Линейная цепочка рейса. Отмена вне её — это выход, а не шаг назад. */
export const ORDER_STATUS_CHAIN = [
    'NEW',
    'LOADING',
    'IN_TRANSIT',
    'UNLOADING',
    'DONE',
];
/**
 * Куда можно откатиться с текущего статуса.
 *
 * Статус ставится пальцем в кабине, промахнуться легко, поэтому назад
 * пускаем на любой предыдущий шаг цепочки. Отменённый рейс не откатывается:
 * груз уже вернулся в ленту и мог уйти другому.
 */
export function rollbackTargets(current) {
    const i = ORDER_STATUS_CHAIN.indexOf(current);
    return i <= 0 ? [] : ORDER_STATUS_CHAIN.slice(0, i);
}
/**
 * Страны СНГ (плюс Туркменистан — ассоциированный член, не подписывает
 * часть документов организации, но в грузоперевозках участвует наравне
 * с остальными). Флаг — эмодзи, показывается в карточке груза рядом с
 * городом: на бирже, работающей за пределами одной страны, направление
 * «своя/чужая страна» видно с одного взгляда, без чтения адреса.
 */
export const COUNTRIES = {
    RU: { name: 'Россия', flag: '🇷🇺' },
    BY: { name: 'Беларусь', flag: '🇧🇾' },
    KZ: { name: 'Казахстан', flag: '🇰🇿' },
    UZ: { name: 'Узбекистан', flag: '🇺🇿' },
    KG: { name: 'Киргизия', flag: '🇰🇬' },
    TJ: { name: 'Таджикистан', flag: '🇹🇯' },
    TM: { name: 'Туркменистан', flag: '🇹🇲' },
    AM: { name: 'Армения', flag: '🇦🇲' },
    AZ: { name: 'Азербайджан', flag: '🇦🇿' },
    MD: { name: 'Молдова', flag: '🇲🇩' },
};
/**
 * Флаг страны по коду ISO-2 — для любой страны, не только из справочника.
 *
 * Считается из букв кода: региональные индикаторы Unicode лежат подряд,
 * и «US» превращается в 🇺🇸 арифметикой, без таблицы на две сотни строк,
 * которую пришлось бы дополнять при каждом новом направлении. До
 * свободной географии флаг брался из `COUNTRIES`, где всего десять стран
 * СНГ, — и заявка из Техаса оставалась без флага.
 */
export function countryFlag(code) {
    if (!code || code.length !== 2)
        return '';
    const cc = code.toUpperCase();
    if (!/^[A-Z]{2}$/.test(cc))
        return '';
    return String.fromCodePoint(...[...cc].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
}
/**
 * Название страны по-русски. У стран СНГ берём своё — там привычные
 * «Беларусь» и «Киргизия» вместо официальных форм; для остальных
 * спрашиваем среду выполнения, чтобы не вести двухсотстрочный словарь.
 */
export function countryName(code) {
    if (!code)
        return '';
    const cc = code.toUpperCase();
    if (cc in COUNTRIES)
        return COUNTRIES[cc].name;
    try {
        return new Intl.DisplayNames(['ru'], { type: 'region' }).of(cc) ?? cc;
    }
    catch {
        return cc;
    }
}
/**
 * Виды транспорта.
 *
 * Полностью реализован только автомобильный: под него сделаны кузова,
 * маршрут по дорогам и расчёт расстояния. Остальные включаются
 * администратором по одному — площадка не должна предлагать режим,
 * в котором не умеет довести сделку до конца.
 */
/**
 * Раскладка ленты.
 *
 * `CARDS` — читаемость, `RATE` — упор на выгоду (ставка за километр
 * крупно), `TABLE` — плотность для широкого экрана.
 */
export const FEED_LAYOUTS = ['CARDS', 'RATE', 'TABLE', 'SPLIT'];
export const TRANSPORT_MODES = ['AUTO', 'RAIL', 'SEA', 'AIR'];
export const TRANSPORT_MODE_LABEL = {
    AUTO: 'Автомобиль',
    RAIL: 'Железная дорога',
    SEA: 'Море и река',
    AIR: 'Авиа',
};
export const TRANSPORT_MODE_HINT = {
    AUTO: 'Фуры, тенты, рефрижераторы — основной режим площадки.',
    RAIL: 'Вагоны и контейнеры по железной дороге.',
    SEA: 'Морские и речные суда, контейнерные линии.',
    AIR: 'Авиаперевозка: быстро и дорого, обычно срочный или ценный груз.',
};
/**
 * Спрашивать ли тип кузова. Кузов — свойство автомобиля; у вагона,
 * судна и самолёта на его месте совсем другие характеристики, и
 * подсовывать «тент» в авиазаявке значит собирать заведомо ложные данные.
 */
export function usesBodyTypes(mode) {
    return mode === 'AUTO';
}
/**
 * Считать ли расстояние по дорогам.
 *
 * Только для автомобиля: между портами и аэропортами дорожной сети нет,
 * и маршрутизатор либо откажет, либо проложит бессмысленный сухопутный
 * объезд. Для остальных режимов расстояние по прямой — честное
 * приближение, а не заглушка.
 */
export function usesRoadDistance(mode) {
    return mode === 'AUTO';
}
/**
 * Приведение названия населённого пункта к единому виду.
 *
 * «г.Астана», «г. Астана», «гор. Астана», «город Астана», «астана» —
 * это одно и то же место, но в ленте, в поиске «Ещё по этому направлению»
 * и в сравнении маршрутов они не совпадают, и заявки по одному направлению
 * разъезжаются по разным «направлениям».
 *
 * Срезаем только однозначные формы типа населённого пункта в начале
 * (там, где уверены на 100%: `г.`, `гор.`, `город`, `пгт`, `пос.`,
 * `посёлок`, `рп`), схлопываем пробелы и поднимаем первую букву. Всё
 * остальное — регистр внутри, дефисы, «имени …» — не трогаем: тут легко
 * испортить настоящее имя.
 */
export function normalizeCity(raw) {
    let s = (raw ?? '').trim().replace(/\s+/g, ' ');
    if (!s)
        return s;
    // Тип населённого пункта в начале: либо с точкой (`г.Астана`, `пос. …`),
    // либо отдельным словом (`город Астана`). «Грозный», «Горно-Алтайск»,
    // «Гори» не задеваются — после приставки нет ни точки, ни пробела.
    s = s
        .replace(/^(?:г|гор|город|пгт|пос|посёлок|поселок|рп)(?:\.\s*|\s+)/i, '')
        .trim();
    if (!s)
        return raw.trim();
    if (/[a-zа-яё]/.test(s[0]))
        s = s[0].toUpperCase() + s.slice(1);
    return s;
}
/**
 * Опорные города с координатами.
 *
 * Временная замена геокодеру: до подключения адресного справочника заявки
 * привязываются к крупным узлам. Живёт здесь, а не в интерфейсе, потому что
 * координаты нужны и при публикации груза, и при объявлении свободного
 * места — а две копии этой таблицы разъедутся на первой же правке.
 *
 * Список расширен 31 августа 2026 до 261 города по всем странам СНГ —
 * все областные/республиканские центры России плюс значимые вторые города
 * (Дзержинск, Кстово и подобные), административные центры регионов
 * остальных стран СНГ. Источник — открытые данные GeoNames (`geonames.org`,
 * дампы по странам + `admin1CodesASCII.txt`), русские названия городов и
 * регионов сверены и подставлены вручную (в дампе GeoNames для этого
 * региона они даны транслитерацией, кириллица — только для части городов
 * и через неоднозначный список альтернативных имён, которому нельзя
 * доверять автоматически — так в первой попытке в список чуть не попало
 * «Горький» вместо «Нижний Новгород»). Список даже так не исчерпывающий
 * (не каждый посёлок — не задача биржи), но на порядок полнее прежних
 * 10 городов.
 */
export const CITIES = {
    // --- Россия ---
    Абакан: { region: 'Хакасия', country: 'RU', lat: 53.7154, lng: 91.4259 },
    Альметьевск: { region: 'Татарстан', country: 'RU', lat: 54.904, lng: 52.3179 },
    Ангарск: { region: 'Иркутская обл.', country: 'RU', lat: 52.5597, lng: 103.9141 },
    Арзамас: { region: 'Нижегородская обл.', country: 'RU', lat: 55.3956, lng: 43.8381 },
    Армавир: { region: 'Краснодарский край', country: 'RU', lat: 44.9985, lng: 41.1147 },
    Архангельск: { region: 'Архангельская обл.', country: 'RU', lat: 64.5461, lng: 40.5518 },
    Астрахань: { region: 'Астраханская обл.', country: 'RU', lat: 46.3497, lng: 48.0408 },
    Ачинск: { region: 'Красноярский край', country: 'RU', lat: 56.2679, lng: 90.5015 },
    Балаково: { region: 'Саратовская обл.', country: 'RU', lat: 52.0264, lng: 47.7967 },
    Балашиха: { region: 'Московская обл.', country: 'RU', lat: 55.7948, lng: 37.9479 },
    Барнаул: { region: 'Алтайский край', country: 'RU', lat: 53.362, lng: 83.7279 },
    Батайск: { region: 'Ростовская обл.', country: 'RU', lat: 47.1375, lng: 39.7571 },
    Белгород: { region: 'Белгородская обл.', country: 'RU', lat: 50.6034, lng: 36.5809 },
    Березники: { region: 'Пермский край', country: 'RU', lat: 59.4091, lng: 56.8204 },
    Бийск: { region: 'Алтайский край', country: 'RU', lat: 52.5342, lng: 85.1966 },
    Благовещенск: { region: 'Амурская обл.', country: 'RU', lat: 50.2759, lng: 127.5264 },
    Братск: { region: 'Иркутская обл.', country: 'RU', lat: 56.1325, lng: 101.6142 },
    Брянск: { region: 'Брянская обл.', country: 'RU', lat: 53.271, lng: 34.3214 },
    'Великие Луки': { region: 'Псковская обл.', country: 'RU', lat: 56.3406, lng: 30.5438 },
    'Великий Новгород': { region: 'Новгородская обл.', country: 'RU', lat: 58.5213, lng: 31.271 },
    Владивосток: { region: 'Приморский край', country: 'RU', lat: 43.1056, lng: 131.8735 },
    Владикавказ: { region: 'Северная Осетия', country: 'RU', lat: 43.041, lng: 44.6699 },
    Владимир: { region: 'Владимирская обл.', country: 'RU', lat: 56.1385, lng: 40.3998 },
    Волгоград: { region: 'Волгоградская обл.', country: 'RU', lat: 48.7138, lng: 44.4976 },
    Волгодонск: { region: 'Ростовская обл.', country: 'RU', lat: 47.5114, lng: 42.1527 },
    Волжский: { region: 'Волгоградская обл.', country: 'RU', lat: 48.7858, lng: 44.7797 },
    Вологда: { region: 'Вологодская обл.', country: 'RU', lat: 59.2239, lng: 39.884 },
    Воронеж: { region: 'Воронежская обл.', country: 'RU', lat: 51.6683, lng: 39.192 },
    Грозный: { region: 'Чечня', country: 'RU', lat: 43.312, lng: 45.689 },
    Дербент: { region: 'Дагестан', country: 'RU', lat: 42.0662, lng: 48.2876 },
    Дзержинск: { region: 'Нижегородская обл.', country: 'RU', lat: 56.2442, lng: 43.4554 },
    Димитровград: { region: 'Ульяновская обл.', country: 'RU', lat: 54.2139, lng: 49.6184 },
    Екатеринбург: { region: 'Свердловская обл.', country: 'RU', lat: 56.8573, lng: 60.6153 },
    Елец: { region: 'Липецкая обл.', country: 'RU', lat: 52.6144, lng: 38.5094 },
    Железнодорожный: { region: 'Московская обл.', country: 'RU', lat: 55.744, lng: 38.0168 },
    Златоуст: { region: 'Челябинская обл.', country: 'RU', lat: 55.1718, lng: 59.6547 },
    Иваново: { region: 'Ивановская обл.', country: 'RU', lat: 56.9999, lng: 40.9726 },
    Ижевск: { region: 'Удмуртия', country: 'RU', lat: 56.8523, lng: 53.1986 },
    Иркутск: { region: 'Иркутская обл.', country: 'RU', lat: 52.2957, lng: 104.2908 },
    'Йошкар-Ола': { region: 'Марий Эл', country: 'RU', lat: 56.6388, lng: 47.8908 },
    Казань: { region: 'Татарстан', country: 'RU', lat: 55.7887, lng: 49.1221 },
    Калининград: { region: 'Калининградская обл.', country: 'RU', lat: 54.7064, lng: 20.511 },
    Калуга: { region: 'Калужская обл.', country: 'RU', lat: 54.5306, lng: 36.27 },
    'Каменск-Уральский': { region: 'Свердловская обл.', country: 'RU', lat: 56.4063, lng: 61.9335 },
    Камышин: { region: 'Волгоградская обл.', country: 'RU', lat: 50.0885, lng: 45.4128 },
    Кемерово: { region: 'Кемеровская обл.', country: 'RU', lat: 55.3542, lng: 86.1044 },
    Киров: { region: 'Кировская обл.', country: 'RU', lat: 58.5981, lng: 49.6578 },
    Киселёвск: { region: 'Кемеровская обл.', country: 'RU', lat: 53.99, lng: 86.6621 },
    Кисловодск: { region: 'Ставропольский край', country: 'RU', lat: 43.9133, lng: 42.7208 },
    Ковров: { region: 'Владимирская обл.', country: 'RU', lat: 56.3575, lng: 41.3189 },
    Коломна: { region: 'Московская обл.', country: 'RU', lat: 55.0711, lng: 38.784 },
    'Комсомольск-на-Амуре': { region: 'Хабаровский край', country: 'RU', lat: 50.5503, lng: 137.01 },
    Королёв: { region: 'Московская обл.', country: 'RU', lat: 55.9142, lng: 37.8256 },
    Кострома: { region: 'Костромская обл.', country: 'RU', lat: 57.7664, lng: 40.9283 },
    Краснодар: { region: 'Краснодарский край', country: 'RU', lat: 45.0453, lng: 38.9818 },
    Красноярск: { region: 'Красноярский край', country: 'RU', lat: 56.0374, lng: 92.9314 },
    Кстово: { region: 'Нижегородская обл.', country: 'RU', lat: 56.1475, lng: 44.1987 },
    Курган: { region: 'Курганская обл.', country: 'RU', lat: 55.4491, lng: 65.3434 },
    Курск: { region: 'Курская обл.', country: 'RU', lat: 51.7269, lng: 36.1846 },
    Кызыл: { region: 'Тыва', country: 'RU', lat: 51.7111, lng: 94.4378 },
    'Ленинск-Кузнецкий': { region: 'Кемеровская обл.', country: 'RU', lat: 54.6567, lng: 86.1737 },
    Липецк: { region: 'Липецкая обл.', country: 'RU', lat: 52.5876, lng: 39.5515 },
    Люберцы: { region: 'Московская обл.', country: 'RU', lat: 55.6772, lng: 37.8932 },
    Магнитогорск: { region: 'Челябинская обл.', country: 'RU', lat: 53.3981, lng: 59.0066 },
    Майкоп: { region: 'Адыгея', country: 'RU', lat: 44.6079, lng: 40.1024 },
    Махачкала: { region: 'Дагестан', country: 'RU', lat: 42.9778, lng: 47.5003 },
    Миасс: { region: 'Челябинская обл.', country: 'RU', lat: 55.0455, lng: 60.1076 },
    Москва: { region: 'Москва', country: 'RU', lat: 55.752, lng: 37.6178 },
    Мурманск: { region: 'Мурманская обл.', country: 'RU', lat: 68.9678, lng: 33.0992 },
    Муром: { region: 'Владимирская обл.', country: 'RU', lat: 55.5685, lng: 42.0239 },
    Мытищи: { region: 'Московская обл.', country: 'RU', lat: 55.911, lng: 37.7296 },
    'Набережные Челны': { region: 'Татарстан', country: 'RU', lat: 55.7372, lng: 52.4196 },
    Назрань: { region: 'Ингушетия', country: 'RU', lat: 43.226, lng: 44.7732 },
    Нальчик: { region: 'Кабардино-Балкария', country: 'RU', lat: 43.4981, lng: 43.6189 },
    Находка: { region: 'Приморский край', country: 'RU', lat: 42.8436, lng: 132.9183 },
    Невинномысск: { region: 'Ставропольский край', country: 'RU', lat: 44.6333, lng: 41.9444 },
    Нефтекамск: { region: 'Башкортостан', country: 'RU', lat: 56.0888, lng: 54.2638 },
    Нефтеюганск: { region: 'Ханты-Мансийский АО', country: 'RU', lat: 61.0998, lng: 72.6035 },
    Нижневартовск: { region: 'Ханты-Мансийский АО', country: 'RU', lat: 60.9344, lng: 76.5531 },
    Нижнекамск: { region: 'Татарстан', country: 'RU', lat: 55.6379, lng: 51.815 },
    'Нижний Новгород': { region: 'Нижегородская обл.', country: 'RU', lat: 56.3287, lng: 44.002 },
    'Нижний Тагил': { region: 'Свердловская обл.', country: 'RU', lat: 57.9194, lng: 59.965 },
    Новокузнецк: { region: 'Кемеровская обл.', country: 'RU', lat: 53.7575, lng: 87.136 },
    Новокуйбышевск: { region: 'Самарская обл.', country: 'RU', lat: 53.0971, lng: 49.94 },
    Новомосковск: { region: 'Тульская обл.', country: 'RU', lat: 54.011, lng: 38.2908 },
    Новороссийск: { region: 'Краснодарский край', country: 'RU', lat: 44.7319, lng: 37.7618 },
    Новосибирск: { region: 'Новосибирская обл.', country: 'RU', lat: 55.0226, lng: 82.9317 },
    Новотроицк: { region: 'Оренбургская обл.', country: 'RU', lat: 51.201, lng: 58.3132 },
    Новочебоксарск: { region: 'Чувашия', country: 'RU', lat: 56.111, lng: 47.4776 },
    Новочеркасск: { region: 'Ростовская обл.', country: 'RU', lat: 47.4222, lng: 40.0937 },
    Ногинск: { region: 'Московская обл.', country: 'RU', lat: 55.8649, lng: 38.4485 },
    Норильск: { region: 'Красноярский край', country: 'RU', lat: 69.3535, lng: 88.2027 },
    Ноябрьск: { region: 'Ямало-Ненецкий АО', country: 'RU', lat: 63.1931, lng: 75.4373 },
    Обнинск: { region: 'Калужская обл.', country: 'RU', lat: 55.1099, lng: 36.6124 },
    Одинцово: { region: 'Московская обл.', country: 'RU', lat: 55.6698, lng: 37.2772 },
    Октябрьский: { region: 'Башкортостан', country: 'RU', lat: 54.4815, lng: 53.471 },
    Омск: { region: 'Омская обл.', country: 'RU', lat: 54.9924, lng: 73.3686 },
    Орёл: { region: 'Орловская обл.', country: 'RU', lat: 52.9688, lng: 36.0791 },
    Оренбург: { region: 'Оренбургская обл.', country: 'RU', lat: 51.7671, lng: 55.0988 },
    'Орехово-Зуево': { region: 'Московская обл.', country: 'RU', lat: 55.8124, lng: 38.9915 },
    Орск: { region: 'Оренбургская обл.', country: 'RU', lat: 51.2321, lng: 58.488 },
    Пенза: { region: 'Пензенская обл.', country: 'RU', lat: 53.1957, lng: 45.0108 },
    Первоуральск: { region: 'Свердловская обл.', country: 'RU', lat: 56.9053, lng: 59.9436 },
    Пермь: { region: 'Пермский край', country: 'RU', lat: 58.0105, lng: 56.2502 },
    Петрозаводск: { region: 'Карелия', country: 'RU', lat: 61.7849, lng: 34.3469 },
    'Петропавловск-Камчатский': {
        region: 'Камчатский край',
        country: 'RU',
        lat: 53.0639,
        lng: 158.6275,
    },
    Подольск: { region: 'Московская обл.', country: 'RU', lat: 55.4242, lng: 37.5547 },
    Прокопьевск: { region: 'Кемеровская обл.', country: 'RU', lat: 53.9152, lng: 86.7189 },
    Псков: { region: 'Псковская обл.', country: 'RU', lat: 57.8192, lng: 28.3318 },
    Пушкино: { region: 'Московская обл.', country: 'RU', lat: 55.9946, lng: 37.829 },
    Пятигорск: { region: 'Ставропольский край', country: 'RU', lat: 44.05, lng: 43.0504 },
    'Ростов-на-Дону': { region: 'Ростовская обл.', country: 'RU', lat: 47.22, lng: 39.7077 },
    Рубцовск: { region: 'Алтайский край', country: 'RU', lat: 51.5147, lng: 81.2061 },
    Рыбинск: { region: 'Ярославская обл.', country: 'RU', lat: 58.0456, lng: 38.8381 },
    Рязань: { region: 'Рязанская обл.', country: 'RU', lat: 54.627, lng: 39.7042 },
    Салават: { region: 'Башкортостан', country: 'RU', lat: 53.3828, lng: 55.9109 },
    Самара: { region: 'Самарская обл.', country: 'RU', lat: 53.2077, lng: 50.1355 },
    'Санкт-Петербург': { region: 'Санкт-Петербург', country: 'RU', lat: 59.9386, lng: 30.3141 },
    Саранск: { region: 'Мордовия', country: 'RU', lat: 54.1849, lng: 45.1717 },
    Саратов: { region: 'Саратовская обл.', country: 'RU', lat: 51.5405, lng: 45.9901 },
    Северодвинск: { region: 'Архангельская обл.', country: 'RU', lat: 64.5583, lng: 39.8297 },
    Северск: { region: 'Томская обл.', country: 'RU', lat: 56.6006, lng: 84.8864 },
    'Сергиев Посад': { region: 'Московская обл.', country: 'RU', lat: 56.312, lng: 38.1387 },
    Серпухов: { region: 'Московская обл.', country: 'RU', lat: 54.9198, lng: 37.4162 },
    Смоленск: { region: 'Смоленская обл.', country: 'RU', lat: 54.7783, lng: 32.0509 },
    Сочи: { region: 'Краснодарский край', country: 'RU', lat: 43.597, lng: 39.7248 },
    Ставрополь: { region: 'Ставропольский край', country: 'RU', lat: 45.0344, lng: 41.9642 },
    'Старый Оскол': { region: 'Белгородская обл.', country: 'RU', lat: 51.3025, lng: 37.8461 },
    Стерлитамак: { region: 'Башкортостан', country: 'RU', lat: 53.6379, lng: 55.9533 },
    Сургут: { region: 'Ханты-Мансийский АО', country: 'RU', lat: 61.2576, lng: 73.4178 },
    Сызрань: { region: 'Самарская обл.', country: 'RU', lat: 53.1585, lng: 48.4681 },
    Сыктывкар: { region: 'Коми', country: 'RU', lat: 61.6639, lng: 50.8163 },
    Таганрог: { region: 'Ростовская обл.', country: 'RU', lat: 47.2363, lng: 38.9053 },
    Тамбов: { region: 'Тамбовская обл.', country: 'RU', lat: 52.7363, lng: 41.441 },
    Тверь: { region: 'Тверская обл.', country: 'RU', lat: 56.8584, lng: 35.9006 },
    Тобольск: { region: 'Тюменская обл.', country: 'RU', lat: 58.1981, lng: 68.2546 },
    Тольятти: { region: 'Самарская обл.', country: 'RU', lat: 53.5303, lng: 49.3461 },
    Томск: { region: 'Томская обл.', country: 'RU', lat: 56.5005, lng: 84.9822 },
    Тула: { region: 'Тульская обл.', country: 'RU', lat: 54.1961, lng: 37.6182 },
    Тюмень: { region: 'Тюменская обл.', country: 'RU', lat: 57.1522, lng: 65.5272 },
    'Улан-Удэ': { region: 'Бурятия', country: 'RU', lat: 51.8265, lng: 107.5998 },
    Ульяновск: { region: 'Ульяновская обл.', country: 'RU', lat: 54.3282, lng: 48.3866 },
    Уссурийск: { region: 'Приморский край', country: 'RU', lat: 43.8047, lng: 131.9573 },
    Уфа: { region: 'Башкортостан', country: 'RU', lat: 54.7431, lng: 55.9678 },
    Хабаровск: { region: 'Хабаровский край', country: 'RU', lat: 48.462, lng: 135.0971 },
    Хасавюрт: { region: 'Дагестан', country: 'RU', lat: 43.2487, lng: 46.5857 },
    Химки: { region: 'Московская обл.', country: 'RU', lat: 55.9001, lng: 37.4285 },
    Чебоксары: { region: 'Чувашия', country: 'RU', lat: 56.1322, lng: 47.246 },
    Челябинск: { region: 'Челябинская обл.', country: 'RU', lat: 55.1611, lng: 61.4288 },
    Череповец: { region: 'Вологодская обл.', country: 'RU', lat: 59.1333, lng: 37.9 },
    Черкесск: { region: 'Карачаево-Черкесия', country: 'RU', lat: 44.2238, lng: 42.0462 },
    Чита: { region: 'Забайкальский край', country: 'RU', lat: 52.0431, lng: 113.4917 },
    Шахты: { region: 'Ростовская обл.', country: 'RU', lat: 47.7192, lng: 40.216 },
    Щёлково: { region: 'Московская обл.', country: 'RU', lat: 55.925, lng: 37.9722 },
    Электросталь: { region: 'Московская обл.', country: 'RU', lat: 55.7865, lng: 38.4571 },
    Элиста: { region: 'Калмыкия', country: 'RU', lat: 46.3079, lng: 44.2554 },
    Энгельс: { region: 'Саратовская обл.', country: 'RU', lat: 51.4839, lng: 46.1053 },
    'Южно-Сахалинск': { region: 'Сахалинская обл.', country: 'RU', lat: 46.9543, lng: 142.7356 },
    Якутск: { region: 'Якутия', country: 'RU', lat: 62.0311, lng: 129.7229 },
    Ярославль: { region: 'Ярославская обл.', country: 'RU', lat: 57.6299, lng: 39.8737 },
    // --- Беларусь ---
    Барановичи: { region: 'Брестская обл.', country: 'BY', lat: 53.1326, lng: 26.0078 },
    Бобруйск: { region: 'Могилёвская обл.', country: 'BY', lat: 53.1468, lng: 29.2055 },
    Борисов: { region: 'Минская обл.', country: 'BY', lat: 54.2279, lng: 28.505 },
    Брест: { region: 'Брестская обл.', country: 'BY', lat: 52.1089, lng: 23.7175 },
    Витебск: { region: 'Витебская обл.', country: 'BY', lat: 55.1904, lng: 30.2049 },
    Гомель: { region: 'Гомельская обл.', country: 'BY', lat: 52.4345, lng: 30.9754 },
    Гродно: { region: 'Гродненская обл.', country: 'BY', lat: 53.6758, lng: 23.8289 },
    Лида: { region: 'Гродненская обл.', country: 'BY', lat: 53.8833, lng: 25.2997 },
    Минск: { region: 'Минская обл.', country: 'BY', lat: 53.9002, lng: 27.5665 },
    Могилёв: { region: 'Могилёвская обл.', country: 'BY', lat: 53.9088, lng: 30.3404 },
    Мозырь: { region: 'Гомельская обл.', country: 'BY', lat: 52.0416, lng: 29.2163 },
    Молодечно: { region: 'Минская обл.', country: 'BY', lat: 54.3167, lng: 26.854 },
    Новополоцк: { region: 'Витебская обл.', country: 'BY', lat: 55.5262, lng: 28.5931 },
    Орша: { region: 'Витебская обл.', country: 'BY', lat: 54.5136, lng: 30.4037 },
    Пинск: { region: 'Брестская обл.', country: 'BY', lat: 52.1215, lng: 26.0673 },
    Солигорск: { region: 'Минская обл.', country: 'BY', lat: 52.7898, lng: 27.5358 },
    // --- Казахстан ---
    Актобе: { region: 'Актюбинская обл.', country: 'KZ', lat: 50.2797, lng: 57.2072 },
    Алматы: { region: 'Алматинская обл.', country: 'KZ', lat: 43.2525, lng: 76.9115 },
    Астана: { region: 'г. Астана', country: 'KZ', lat: 51.1801, lng: 71.446 },
    Атырау: { region: 'Атырауская обл.', country: 'KZ', lat: 47.1048, lng: 51.8843 },
    Караганда: { region: 'Карагандинская обл.', country: 'KZ', lat: 49.8019, lng: 73.1021 },
    Костанай: { region: 'Костанайская обл.', country: 'KZ', lat: 53.2144, lng: 63.6246 },
    Кызылорда: { region: 'Кызылординская обл.', country: 'KZ', lat: 44.8528, lng: 65.5092 },
    Павлодар: { region: 'Павлодарская обл.', country: 'KZ', lat: 52.276, lng: 76.9688 },
    Петропавловск: {
        region: 'Северо-Казахстанская обл.',
        country: 'KZ',
        lat: 54.8734,
        lng: 69.1507,
    },
    Семей: { region: 'Абайская обл.', country: 'KZ', lat: 50.4206, lng: 80.2502 },
    Тараз: { region: 'Жамбылская обл.', country: 'KZ', lat: 42.898, lng: 71.3733 },
    Темиртау: { region: 'Карагандинская обл.', country: 'KZ', lat: 50.052, lng: 72.955 },
    Туркестан: { region: 'Туркестанская обл.', country: 'KZ', lat: 43.2946, lng: 68.2569 },
    Уральск: { region: 'Западно-Казахстанская обл.', country: 'KZ', lat: 51.246, lng: 51.4256 },
    'Усть-Каменогорск': {
        region: 'Восточно-Казахстанская обл.',
        country: 'KZ',
        lat: 49.9714,
        lng: 82.6059,
    },
    Шымкент: { region: 'Туркестанская обл.', country: 'KZ', lat: 42.3099, lng: 69.6004 },
    // --- Узбекистан ---
    Ангрен: { region: 'Ташкентская обл.', country: 'UZ', lat: 41.0167, lng: 70.1436 },
    Андижан: { region: 'Андижанская обл.', country: 'UZ', lat: 40.7834, lng: 72.3507 },
    Бухара: { region: 'Бухарская обл.', country: 'UZ', lat: 39.7703, lng: 64.4307 },
    Джизак: { region: 'Джизакская обл.', country: 'UZ', lat: 40.1335, lng: 67.8296 },
    Карши: { region: 'Кашкадарьинская обл.', country: 'UZ', lat: 38.8606, lng: 65.7891 },
    Коканд: { region: 'Ферганская обл.', country: 'UZ', lat: 40.5286, lng: 70.9425 },
    Маргилан: { region: 'Ферганская обл.', country: 'UZ', lat: 40.4724, lng: 71.7246 },
    Наманган: { region: 'Наманганская обл.', country: 'UZ', lat: 40.9983, lng: 71.6726 },
    Нукус: { region: 'Каракалпакстан', country: 'UZ', lat: 42.4586, lng: 59.6058 },
    Самарканд: { region: 'Самаркандская обл.', country: 'UZ', lat: 39.6546, lng: 66.9645 },
    Ташкент: { region: 'Ташкентская обл.', country: 'UZ', lat: 41.2647, lng: 69.2163 },
    Термез: { region: 'Сурхандарьинская обл.', country: 'UZ', lat: 37.2242, lng: 67.2783 },
    Фергана: { region: 'Ферганская обл.', country: 'UZ', lat: 40.3842, lng: 71.7843 },
    Чирчик: { region: 'Ташкентская обл.', country: 'UZ', lat: 41.4689, lng: 69.5822 },
    // --- Киргизия ---
    Балыкчы: { region: 'Иссык-Кульская обл.', country: 'KG', lat: 42.4602, lng: 76.1871 },
    Бишкек: { region: 'Чуйская обл.', country: 'KG', lat: 42.87, lng: 74.59 },
    'Кара-Балта': { region: 'Чуйская обл.', country: 'KG', lat: 42.8142, lng: 73.8481 },
    Каракол: { region: 'Иссык-Кульская обл.', country: 'KG', lat: 42.4905, lng: 78.392 },
    Нарын: { region: 'Нарынская обл.', country: 'KG', lat: 41.4283, lng: 75.9957 },
    Ош: { region: 'Ошская обл.', country: 'KG', lat: 40.5283, lng: 72.7985 },
    Талас: { region: 'Таласская обл.', country: 'KG', lat: 42.5226, lng: 72.2417 },
    Токмок: { region: 'Чуйская обл.', country: 'KG', lat: 42.8419, lng: 75.3015 },
    Узген: { region: 'Ошская обл.', country: 'KG', lat: 40.7699, lng: 73.3007 },
    // --- Таджикистан ---
    Бохтар: { region: 'Хатлонская обл.', country: 'TJ', lat: 37.8365, lng: 68.7776 },
    Вахдат: { region: 'РРП', country: 'TJ', lat: 38.5563, lng: 69.0135 },
    Душанбе: { region: 'г. Душанбе', country: 'TJ', lat: 38.5358, lng: 68.7791 },
    Истаравшан: { region: 'Согдийская обл.', country: 'TJ', lat: 39.9142, lng: 69.0033 },
    Исфара: { region: 'Согдийская обл.', country: 'TJ', lat: 40.1265, lng: 70.6253 },
    Кайраккум: { region: 'Согдийская обл.', country: 'TJ', lat: 40.266, lng: 69.7959 },
    Канибадам: { region: 'Согдийская обл.', country: 'TJ', lat: 40.2941, lng: 70.4312 },
    Куляб: { region: 'Хатлонская обл.', country: 'TJ', lat: 37.9146, lng: 69.7845 },
    Турсунзаде: { region: 'РРП', country: 'TJ', lat: 38.5127, lng: 68.2316 },
    Худжанд: { region: 'Согдийская обл.', country: 'TJ', lat: 40.2826, lng: 69.6222 },
    // --- Туркменистан ---
    Ашхабад: { region: 'г. Ашхабад', country: 'TM', lat: 37.95, lng: 58.3833 },
    Байрамали: { region: 'Марыйская обл.', country: 'TM', lat: 37.6185, lng: 62.1672 },
    Балканабат: { region: 'Балканская обл.', country: 'TM', lat: 39.5108, lng: 54.3671 },
    Дашогуз: { region: 'Дашогузская обл.', country: 'TM', lat: 41.8363, lng: 59.9666 },
    Мары: { region: 'Марыйская обл.', country: 'TM', lat: 37.5938, lng: 61.8303 },
    Теджен: { region: 'Ахалская обл.', country: 'TM', lat: 37.3834, lng: 60.5055 },
    Туркменабат: { region: 'Лебапская обл.', country: 'TM', lat: 39.0733, lng: 63.5786 },
    Туркменбаши: { region: 'Балканская обл.', country: 'TM', lat: 40.0222, lng: 52.9552 },
    // --- Армения ---
    Абовян: { region: 'Котайкская обл.', country: 'AM', lat: 40.2717, lng: 44.6334 },
    'Вагаршапат (Эчмиадзин)': {
        region: 'Армавирская обл.',
        country: 'AM',
        lat: 40.1656,
        lng: 44.2946,
    },
    Ванадзор: { region: 'Лорийская обл.', country: 'AM', lat: 40.8074, lng: 44.497 },
    Гюмри: { region: 'Ширакская обл.', country: 'AM', lat: 40.7931, lng: 43.8464 },
    Ереван: { region: 'г. Ереван', country: 'AM', lat: 40.1777, lng: 44.5126 },
    Раздан: { region: 'Котайкская обл.', country: 'AM', lat: 40.5169, lng: 44.7559 },
    // --- Азербайджан ---
    Баку: { region: 'г. Баку', country: 'AZ', lat: 40.3777, lng: 49.892 },
    Гянджа: { region: 'Гянджа-Газахский р-н', country: 'AZ', lat: 40.6816, lng: 46.3613 },
    Евлах: { region: 'Ширванская обл.', country: 'AZ', lat: 40.6183, lng: 47.1501 },
    Ленкорань: { region: 'Ленкоранская обл.', country: 'AZ', lat: 38.7543, lng: 48.8506 },
    Мингечевир: { region: 'Аранская обл.', country: 'AZ', lat: 40.7642, lng: 47.0623 },
    Нахичевань: { region: 'Нахичеванская АР', country: 'AZ', lat: 39.2089, lng: 45.4122 },
    Сумгаит: { region: 'Абшеронская обл.', country: 'AZ', lat: 40.5897, lng: 49.6686 },
    Хачмаз: { region: 'Губа-Хачмазская обл.', country: 'AZ', lat: 41.4643, lng: 48.8057 },
    Хырдалан: { region: 'Абшеронская обл.', country: 'AZ', lat: 40.4481, lng: 49.755 },
    Шеки: { region: 'Шеки-Загатальская обл.', country: 'AZ', lat: 41.1919, lng: 47.1706 },
    Ширван: { region: 'Ширванская обл.', country: 'AZ', lat: 39.9378, lng: 48.929 },
    // --- Молдова ---
    Бельцы: { region: 'муниципий Бельцы', country: 'MD', lat: 47.7629, lng: 27.9285 },
    Бендеры: { region: 'Приднестровье', country: 'MD', lat: 46.8316, lng: 29.4767 },
    Единец: { region: 'Единецкий р-н', country: 'MD', lat: 48.1732, lng: 27.3008 },
    Кагул: { region: 'Кагульский р-н', country: 'MD', lat: 45.9046, lng: 28.1952 },
    Кишинёв: { region: 'муниципий Кишинёв', country: 'MD', lat: 47.009, lng: 28.8594 },
    Комрат: { region: 'Гагаузия', country: 'MD', lat: 46.2949, lng: 28.6571 },
    Оргеев: { region: 'Оргеевский р-н', country: 'MD', lat: 47.385, lng: 28.8254 },
    Рыбница: { region: 'Приднестровье', country: 'MD', lat: 47.7682, lng: 29.01 },
    Сорока: { region: 'Сорокский р-н', country: 'MD', lat: 48.1566, lng: 28.2849 },
    Тирасполь: { region: 'Приднестровье', country: 'MD', lat: 46.8428, lng: 29.6284 },
    Унгены: { region: 'Унгенский р-н', country: 'MD', lat: 47.2102, lng: 27.8001 },
};
export const CITY_NAMES = Object.keys(CITIES);
/**
 * Регионы — выводятся из `CITIES`, а не задаются вторым списком.
 *
 * Раньше список жил отдельно (`miniapp/src/regions.ts`) и дважды за одну
 * сессию разъезжался со справочником городов: значения регионов лежат в
 * базе строками и сравниваются точным совпадением (фильтр ленты,
 * белые/чёрные списки групп), поэтому расхождение не падает с ошибкой,
 * а молча прячет грузы. Один источник делает такое расхождение
 * невозможным по устройству.
 */
export const REGIONS = [...new Set(Object.values(CITIES).map((c) => c.region))].sort((a, b) => a.localeCompare(b, 'ru'));
/** Регионы по странам — для выбора «страна → область». */
export function regionsByCountry() {
    const map = new Map();
    for (const c of Object.values(CITIES)) {
        const set = map.get(c.country) ?? new Set();
        map.set(c.country, set);
        set.add(c.region);
    }
    return [...map.entries()]
        .sort(([a], [b]) => a === 'RU' ? -1 : b === 'RU' ? 1 : COUNTRIES[a].name.localeCompare(COUNTRIES[b].name, 'ru'))
        .map(([cc, set]) => [cc, [...set].sort((x, y) => x.localeCompare(y, 'ru'))]);
}
/** Координаты региона по опорному городу. Для подбора попутных грузов. */
export function regionPoint(region) {
    const hit = Object.values(CITIES).find((c) => c.region === region);
    return hit ? { lat: hit.lat, lng: hit.lng } : null;
}
export const ROLES = ['LOGIST', 'DRIVER', 'BOTH'];
/**
 * Должности в команде площадки.
 *
 * Отдельно от `role`: сотрудник поддержки может сам быть перевозчиком,
 * и смешивать эти две вещи в одном поле — значит однажды выдать
 * диспетчерский доступ всем, у кого роль «логист».
 */
export const STAFF_ROLES = ['NONE', 'SUPPORT', 'MODERATOR', 'ADMIN'];
export const STAFF_ROLE_LABEL = {
    NONE: 'Участник',
    SUPPORT: 'Поддержка',
    MODERATOR: 'Модератор',
    ADMIN: 'Администратор',
};
export const STAFF_ROLE_HINT = {
    NONE: 'Обычный участник биржи. Чужие сделки не видит.',
    SUPPORT: 'Видит все рейсы, заявки и машины, ищет по номеру и участнику. ' +
        'Ничего не блокирует и не меняет — только помогает разобраться.',
    MODERATOR: 'Всё, что доступно поддержке, плюс блокировка заявок и участников.',
    ADMIN: 'Полный доступ: настройки площадки, контракты, деньги, выгрузки.',
};
/** Кто из сотрудников видит чужие сделки в диспетчерском режиме. */
export const STAFF_DISPATCH = ['SUPPORT', 'MODERATOR', 'ADMIN'];
/** Кто может блокировать заявки и участников. */
export const STAFF_MODERATION = ['MODERATOR', 'ADMIN'];
export const ROLE_LABEL = {
    LOGIST: 'Логист',
    DRIVER: 'Перевозчик',
    BOTH: 'Логист и перевозчик',
};
export const PROFILE_TYPES = ['CARRIER', 'FORWARDER', 'SHIPPER'];
export const PROFILE_TYPE_LABEL = {
    CARRIER: 'Перевозчик',
    FORWARDER: 'Экспедитор',
    SHIPPER: 'Грузовладелец',
};
export const PROFILE_TYPE_HINT = {
    CARRIER: 'Ищу грузы для перевозки, у меня есть транспорт',
    FORWARDER: 'Ищу грузы или транспорт, в зависимости от потребности',
    SHIPPER: 'У меня есть груз, ищу перевозчиков',
};
/**
 * Профиль деятельности определяет роль в системе.
 *
 * Экспедитор работает с обеих сторон, поэтому получает обе возможности;
 * остальным лишние разделы только мешают.
 */
export const PROFILE_TYPE_ROLE = {
    CARRIER: 'DRIVER',
    FORWARDER: 'BOTH',
    SHIPPER: 'LOGIST',
};
export const profileTypeSchema = z.object({
    profileType: z.enum(PROFILE_TYPES),
});
export const ENTITY_TYPES = [
    'INDIVIDUAL',
    'SELF_EMPLOYED',
    'IE',
    'COMPANY',
];
export const ENTITY_TYPE_LABEL = {
    INDIVIDUAL: 'Физлицо',
    SELF_EMPLOYED: 'Самозанятый',
    IE: 'ИП',
    COMPANY: 'ООО',
};
/** Короткое пояснение для онбординга — что даёт каждый статус. */
export const ENTITY_TYPE_HINT = {
    INDIVIDUAL: 'Без документов. Только наличные, заказчики-юрлица работать не смогут.',
    SELF_EMPLOYED: 'Налог 4–6%, чек в приложении «Мой налог». Юрлица платят на карту легально.',
    IE: 'Безнал, НДС по желанию, доступ к корпоративным заказчикам.',
    COMPANY: 'Полный доступ к тендерам и крупным грузовладельцам.',
};
/** Статусы, при которых заказчик-юрлицо может провести оплату по безналу. */
export const CAN_ACCEPT_BANK_PAYMENT = ['SELF_EMPLOYED', 'IE', 'COMPANY'];
export const CARGO_STATUS_LABEL = {
    PENDING: 'На модерации',
    ACTIVE: 'Активна',
    BOOKED: 'В работе',
    DONE: 'Завершена',
    CANCELLED: 'Снята',
    EXPIRED: 'Просрочена',
    BLOCKED: 'Заблокирована',
};
export const CARGO_SORTS = ['new', 'price', 'pricePerKm', 'distance'];
export const CARGO_SORT_LABEL = {
    new: 'Сначала новые',
    price: 'Дороже',
    pricePerKm: 'Выгоднее за км',
    distance: 'Ближе',
};
/**
 * Вид техники.
 *
 * Прицеп и платформа — не «машина с особым кузовом», а отдельная единица:
 * своего водителя у неё нет, она цепляется к тягачу и переходит от одной
 * машины к другой. Без явного вида в парке заводятся полуприцепы, которые
 * «сами выезжают в рейс».
 */
export const VEHICLE_KINDS = ['TRUCK', 'TRAILER', 'SEMITRAILER', 'PLATFORM'];
export const VEHICLE_KIND_LABEL = {
    TRUCK: 'Машина',
    TRAILER: 'Прицеп',
    SEMITRAILER: 'Полуприцеп',
    PLATFORM: 'Платформа',
};
/** У несамоходной техники нет водителя — её цепляют к тягачу. */
export const TOWED_KINDS = ['TRAILER', 'SEMITRAILER', 'PLATFORM'];
/* ---------- Схемы запросов ---------- */
const bodyType = z.enum(BODY_TYPES);
/* ---------- Валюты ---------- */
/**
 * Валюты площадки: все страны справочника плюс три расчётные.
 *
 * Порядок неслучаен: рубль первым как база учёта, дальше СНГ по обороту,
 * потом расчётные. Он же порядок в выпадающих списках — перебирать
 * пятнадцать пунктов ради тенге никто не станет.
 */
/**
 * Коды валют, которые система вообще умеет назвать.
 *
 * Это не список включённых: включённые лежат в справочнике `Currency` и
 * настраиваются администратором. Здесь — словарь допустимых кодов, чтобы
 * в базу не попал `PYCHI` из опечатки, и чтобы у каждого кода были имя и
 * знак, не завися от того, заполнил ли их кто-то руками.
 */
export const CURRENCIES = [
    'RUB', 'USD', 'EUR', 'USDT',
    'KZT', 'BYN', 'UZS', 'KGS', 'TJS', 'AMD', 'AZN', 'MDL', 'TMT', 'CNY',
];
/**
 * Что включено на новой площадке.
 *
 * Четыре валюты вместо тринадцати — сознательно: набор валют это решение
 * о рынках, а не о коде, и начинать с тринадцати значит обещать работу
 * там, где её ещё нет. Остальные заводятся в справочнике выключенными и
 * включаются щелчком.
 */
export const DEFAULT_DEAL_CURRENCIES = ['RUB', 'USD', 'EUR', 'USDT'];
/** Курс чего ЦБ не котирует — только руками. */
export const MANUAL_ONLY_CURRENCIES = ['USDT'];
export const RATE_SOURCES = ['CBR', 'MANUAL'];
export const RATE_SOURCE_LABEL = {
    CBR: 'Курс ЦБ РФ',
    MANUAL: 'Задан вручную',
};
export const currencyPatchSchema = z.object({
    deal: z.boolean().optional(),
    display: z.boolean().optional(),
    active: z.boolean().optional(),
    rateSource: z.enum(RATE_SOURCES).optional(),
    /** Ноль и отрицательный курс не принимаем: это молчаливое обнуление сделки. */
    manualRate: z.number().positive().max(1_000_000).nullable().optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
});
export const BASE_CURRENCY = 'RUB';
export const CURRENCY_LABEL = {
    USDT: 'Tether USDT',
    RUB: 'Российский рубль',
    KZT: 'Казахстанский тенге',
    BYN: 'Белорусский рубль',
    UZS: 'Узбекский сум',
    KGS: 'Киргизский сом',
    TJS: 'Таджикский сомони',
    AMD: 'Армянский драм',
    AZN: 'Азербайджанский манат',
    MDL: 'Молдавский лей',
    TMT: 'Туркменский манат',
    USD: 'Доллар США',
    EUR: 'Евро',
    CNY: 'Китайский юань',
};
export const CURRENCY_SIGN = {
    USDT: '₮',
    RUB: '₽',
    KZT: '₸',
    BYN: 'Br',
    UZS: 'сум',
    KGS: 'с',
    TJS: 'смн',
    AMD: '֏',
    AZN: '₼',
    MDL: 'L',
    TMT: 'м',
    USD: '$',
    EUR: '€',
    CNY: '¥',
};
/**
 * Валюта по умолчанию для страны отправления.
 *
 * Перевозка внутри Казахстана назначается в тенге, и подставлять логисту
 * рубль значит заставлять его пересчитывать сумму в уме на каждой заявке.
 */
export const COUNTRY_CURRENCY = {
    RU: 'RUB',
    KZ: 'KZT',
    BY: 'BYN',
    UZ: 'UZS',
    KG: 'KGS',
    TJ: 'TJS',
    AM: 'AMD',
    AZ: 'AZN',
    MD: 'MDL',
    TM: 'TMT',
};
export function currencyOfCountry(code) {
    return (code && COUNTRY_CURRENCY[code.toUpperCase()]) || BASE_CURRENCY;
}
export function isCurrency(v) {
    return typeof v === 'string' && CURRENCIES.includes(v);
}
/**
 * Суммы хранятся целыми единицами валюты, без копеек.
 *
 * Так уже устроены все колонки `*Rub` (целые рубли), и заводить дробную
 * часть только ради доллара значило бы переписать весь денежный учёт ради
 * центов, которые в грузоперевозке никто не торгует: ставки называют
 * круглыми числами. Для сума и драма целые единицы — вообще единственный
 * осмысленный вариант.
 */
export function formatMoney(v, currency = BASE_CURRENCY) {
    return `${new Intl.NumberFormat('ru-RU').format(Math.round(v))} ${CURRENCY_SIGN[currency]}`;
}
export function rateToRub(rates, currency) {
    if (currency === BASE_CURRENCY)
        return 1;
    const r = rates[currency];
    return typeof r === 'number' && r > 0 ? r : null;
}
/**
 * Перевести сумму в рубли по известному курсу.
 *
 * Возвращает `null`, а не ноль или саму сумму, когда курса нет: и то и
 * другое молча превратило бы «мы не знаем курс» в конкретную цифру, а
 * именно на этом сейчас теряются деньги в наценке контракта.
 */
export function toRub(v, currency, rates) {
    const r = rateToRub(rates, currency);
    return r === null ? null : Math.round(v * r);
}
/** Перевести из рублей в валюту сделки. */
export function fromRub(rub, currency, rates) {
    const r = rateToRub(rates, currency);
    return r === null ? null : Math.round(rub / r);
}
/* ---------- Торг ---------- */
export const BIDDING_MODES = ['NONE', 'AUCTION'];
export const BIDDING_MODE_LABEL = {
    NONE: 'Обычный отклик',
    AUCTION: 'Аукцион на понижение',
};
export const BIDDING_MODE_HINT = {
    NONE: 'Перевозчик называет свою цену. Чужих ставок он не видит, вы выбираете из откликов.',
    AUCTION: 'Ваша цена — стартовая. Перевозчики понижают её шагом и видят лучшую ставку. Победителя выбираете всё равно вы.',
};
/*
 * Здесь стоял BID_EXTEND_MINUTES — антиснайпинг, сдвигавший срок торгов
 * на пятнадцать минут, если ставка пришла под конец.
 *
 * Убран намеренно, а не забыт. Он защищает цену ценой времени, а время в
 * грузоперевозке — не разменная монета: срок торгов автор назначает не
 * из азарта, а из того, когда машина должна встать под погрузку. Торги,
 * уезжающие вперёд чужими ставками, ломают ему ровно то, ради чего он их
 * объявил, и заодно лишают перевозчика возможности планировать день.
 *
 * Ставка в последнюю секунду при этом становится возможной. Это цена
 * решения, и распоряжается ею автор: срок объявляется с запасом.
 */
/**
 * Наибольшая ставка, которую сейчас примут.
 *
 * Ровно та цифра, что стоит в отказе `bidRejection`, но посчитанная
 * заранее: её подставляют в форму по кнопке «перебить», чтобы перевозчик
 * не подбирал ставку вслепую и не получал отказ там, где форма обещала
 * успех. Правила у обеих функций одни, поэтому и живут рядом.
 *
 * `null` — предложить нечего: ставки упёрлись в ноль.
 */
export function nextBid(opts) {
    if (opts.bestBid === null)
        return opts.startPrice > 0 ? opts.startPrice : null;
    // Свою же ставку улучшают без шага — достаточно быть ниже на единицу.
    const value = opts.bestIsMine
        ? opts.bestBid - 1
        : Math.min(opts.bestBid - opts.step, opts.bestBid - 1);
    return value > 0 ? value : null;
}
/**
 * Можно ли поставить такую ставку в аукционе — и почему нет.
 *
 * Одна функция на сервер и на форму: перевозчик должен видеть отказ до
 * нажатия, а не после. Возвращает причину человеческим языком, потому что
 * «ставка отклонена» без объяснения — худшее, что можно показать в торге.
 */
export function bidRejection(opts) {
    const now = opts.now ?? new Date();
    if (opts.endsAt && opts.endsAt.getTime() <= now.getTime()) {
        return 'Торги по этой заявке завершены';
    }
    if (opts.bid <= 0)
        return 'Укажите ставку';
    if (opts.bestBid === null) {
        return opts.bid > opts.startPrice
            ? `Ставка не может быть выше стартовой цены — ${opts.startPrice}`
            : null;
    }
    // Свою же ставку улучшают без шага: требовать шаг от самого себя
    // означало бы наказывать за то, что ты и так лидируешь.
    if (opts.bestIsMine) {
        return opts.bid >= opts.bestBid
            ? `Ваша ставка уже ${opts.bestBid} — новая должна быть ниже`
            : null;
    }
    /*
     * Потолок — лучшая ставка минус шаг. Отдельная проверка на строгое
     * понижение нужна ради нулевого шага: без неё ставка, равная лучшей,
     * проходила бы, хотя она ничего не улучшает и создаёт ничью там, где
     * должен быть перебой.
     */
    const ceiling = opts.bestBid - opts.step;
    if (opts.bid >= opts.bestBid || opts.bid > ceiling) {
        return `Нужно не выше ${Math.min(ceiling, opts.bestBid - 1)}: лучшая ставка ${opts.bestBid}, шаг ${opts.step}`;
    }
    return null;
}
export const createCargoSchema = z
    .object({
    title: z.string().min(3).max(120),
    weightKg: z.number().int().positive().max(60_000),
    volumeM3: z.number().positive().max(200).optional(),
    transportMode: z.enum(TRANSPORT_MODES).default('AUTO'),
    /*
     * География свободная: точку можно выбрать из справочника, найти
     * геокодером или ткнуть на карте. Поэтому здесь только формат, а не
     * принадлежность к списку — иначе деревня, которой нет в справочнике,
     * снова окажется непубликуемой.
     */
    fromAddress: z.string().min(2).max(200).transform(normalizeCity).pipe(z.string().min(2).max(200)),
    fromRegion: z.string().max(80).default(''),
    fromLat: z.number().min(-90).max(90),
    fromLng: z.number().min(-180).max(180),
    fromCountry: z.string().length(2).nullable().optional(),
    /**
     * Точный адрес забора — улица, дом, ворота, ориентир.
     *
     * Необязателен: на бирже сначала договариваются о направлении, а
     * склад называют уже исполнителю. Виден только сторонам сделки.
     */
    fromAddressDetail: z.string().max(300).nullable().optional(),
    toAddress: z.string().min(2).max(200).transform(normalizeCity).pipe(z.string().min(2).max(200)),
    toRegion: z.string().max(80).default(''),
    toLat: z.number().min(-90).max(90),
    toLng: z.number().min(-180).max(180),
    toCountry: z.string().length(2).nullable().optional(),
    /** Точный адрес доставки. Условия те же, что у адреса забора. */
    toAddressDetail: z.string().max(300).nullable().optional(),
    loadDateFrom: z.coerce.date(),
    loadDateTo: z.coerce.date().optional(),
    bodyTypes: z.array(bodyType).max(BODY_TYPES.length).default([]),
    /*
     * Ставка в валюте сделки, а не в рублях.
     *
     * Поле называется `price`, потому что несёт то, что назвал логист:
     * тенге, сумы или рубли. Оставить имя `priceRub` для суммы в тенге
     * значило бы повторить ложь, из-за которой наценка контракта считала
     * доллары рублями. Рублёвый эквивалент считает сервер по курсу дня.
     *
     * Потолок поднят: десять миллионов рублей — разумный предел ставки,
     * а десять миллионов сумов это меньше семидесяти тысяч рублей.
     */
    price: z.number().int().positive().max(100_000_000_000),
    currency: z.enum(CURRENCIES).default('RUB'),
    biddingMode: z.enum(BIDDING_MODES).default('NONE'),
    /** Обязателен при аукционе: торг без срока не заканчивается никогда. */
    bidEndsAt: z.coerce.date().optional(),
    bidStep: z.number().int().min(0).max(100_000_000).default(0),
    vatIncluded: z.boolean().default(false),
    paymentType: z.enum(PAYMENT_TYPES).default('BANK'),
    delayDays: z.number().int().min(0).max(120).default(0),
    /** Требования к крану. Осмысленны только вместе с кузовом «манипулятор». */
    craneCapacityKg: z.number().int().positive().max(50_000).nullable().optional(),
    craneReachM: z.number().positive().max(60).nullable().optional(),
    /**
     * Догруз: груз занимает часть кузова и едет попутно.
     *
     * Отдельный флаг, а не вывод из веса: пять тонн — это и полная загрузка
     * «газели», и догруз в фуру. Решает отправитель, потому что он же
     * соглашается на то, что машина поедет не напрямую.
     */
    partial: z.boolean().default(false),
    /** Сколько метров кузова займёт. Для догруза важнее веса. */
    lengthM: z.number().positive().max(20).nullable().optional(),
    /** Допустимое отклонение перевозчика от прямого маршрута. */
    detourKm: z.number().int().min(0).max(500).nullable().optional(),
    /** Сколько машин нужно под заявку. */
    vehiclesCount: z.number().int().min(1).max(50).default(1),
    /** Отдельный телефон для связи по заявке. */
    contactPhone: z.string().trim().max(32).nullable().optional(),
    /** Аванс — свободным текстом, как назвали. */
    advanceNote: z.string().trim().max(200).nullable().optional(),
    /** Таможенные условия: «Растаможка в Нукусе». */
    customsNote: z.string().trim().max(300).nullable().optional(),
    /** Сцепка / автопоезд («паровоз»). */
    roadTrain: z.boolean().default(false),
    /** Требуемые способы погрузки/выгрузки. */
    loadingTypes: z.array(z.enum(LOADING_TYPES)).max(LOADING_TYPES.length).default([]),
    comment: z.string().max(1000).optional(),
    /** Через сколько часов заявка протухнет. */
    ttlHours: z.number().int().min(1).max(240).default(72),
    /** Закрытая публикация: груз увидит только эта группа. */
    audienceGroupId: z.string().nullable().optional(),
})
    .refine((v) => !v.loadDateTo || v.loadDateTo >= v.loadDateFrom, {
    message: 'Дата окончания загрузки раньше даты начала',
    path: ['loadDateTo'],
})
    .refine((v) => !usesBodyTypes(v.transportMode ?? 'AUTO') || v.bodyTypes.length > 0, {
    message: 'Выберите хотя бы один тип кузова',
    path: ['bodyTypes'],
});
/**
 * Правка опубликованной заявки.
 *
 * Отдельная схема, а не `.partial()` от создания: у создания стоят
 * `.refine`, а от них `.partial()` не берётся. Правится не всё — вид
 * транспорта и закрытая публикация меняют смысл заявки настолько, что
 * честнее снять её и опубликовать заново, чем переписать под теми же
 * откликами.
 */
export const updateCargoSchema = z
    .object({
    title: z.string().min(3).max(120).optional(),
    weightKg: z.number().int().positive().max(60_000).optional(),
    volumeM3: z.number().positive().max(200).nullable().optional(),
    fromAddress: z.string().min(2).max(200).transform(normalizeCity).pipe(z.string().min(2).max(200)).optional(),
    fromRegion: z.string().max(80).optional(),
    fromLat: z.number().min(-90).max(90).optional(),
    fromLng: z.number().min(-180).max(180).optional(),
    fromCountry: z.string().length(2).nullable().optional(),
    fromAddressDetail: z.string().max(300).nullable().optional(),
    toAddress: z.string().min(2).max(200).transform(normalizeCity).pipe(z.string().min(2).max(200)).optional(),
    toRegion: z.string().max(80).optional(),
    toLat: z.number().min(-90).max(90).optional(),
    toLng: z.number().min(-180).max(180).optional(),
    toCountry: z.string().length(2).nullable().optional(),
    toAddressDetail: z.string().max(300).nullable().optional(),
    loadDateFrom: z.coerce.date().optional(),
    loadDateTo: z.coerce.date().nullable().optional(),
    bodyTypes: z.array(bodyType).max(BODY_TYPES.length).optional(),
    price: z.number().int().positive().max(100_000_000_000).optional(),
    currency: z.enum(CURRENCIES).optional(),
    biddingMode: z.enum(BIDDING_MODES).optional(),
    bidEndsAt: z.coerce.date().nullable().optional(),
    bidStep: z.number().int().min(0).max(100_000_000).optional(),
    vatIncluded: z.boolean().optional(),
    paymentType: z.enum(PAYMENT_TYPES).optional(),
    delayDays: z.number().int().min(0).max(120).optional(),
    craneCapacityKg: z.number().int().positive().max(50_000).nullable().optional(),
    craneReachM: z.number().positive().max(60).nullable().optional(),
    partial: z.boolean().optional(),
    lengthM: z.number().positive().max(20).nullable().optional(),
    detourKm: z.number().int().min(0).max(500).nullable().optional(),
    vehiclesCount: z.number().int().min(1).max(50).optional(),
    contactPhone: z.string().trim().max(32).nullable().optional(),
    advanceNote: z.string().trim().max(200).nullable().optional(),
    customsNote: z.string().trim().max(300).nullable().optional(),
    roadTrain: z.boolean().optional(),
    loadingTypes: z.array(z.enum(LOADING_TYPES)).max(LOADING_TYPES.length).optional(),
    comment: z.string().max(1000).nullable().optional(),
})
    .refine((v) => !v.loadDateTo || !v.loadDateFrom || v.loadDateTo >= v.loadDateFrom, {
    message: 'Дата окончания загрузки раньше даты начала',
    path: ['loadDateTo'],
});
/* ---------- Быстрая заявка из чата бота ---------- */
/**
 * Шаблон для быстрой заявки.
 *
 * Бот отдаёт его на `/newcargo` без текста. Формат нестрогий — парсер
 * (`services/cargoParse.ts`) терпим к вариациям, — но подписи помогают
 * ему не гадать. Человек копирует, заполняет, отправляет боту одним
 * сообщением.
 */
export const CARGO_TEMPLATE = [
    'Откуда — Куда',
    'Груз: ',
    'Вес: т',
    'Кузов: Тент',
    'Погрузка: готов',
    'Ставка: ',
    'Оплата: наличные',
    '',
    '# Необязательно:',
    '# Объём: м3',
    '# Количество: 1 авто',
    '# Аванс: ',
    '# Растаможка: ',
    '# Погрузка/разгрузка: верхняя',
    '# Телефон: ',
    '# Комментарий: ',
].join('\n');
export const cargoDraftTextSchema = z.object({
    text: z.string().min(1).max(4000),
});
export const cargoModerateSchema = z.object({
    decision: z.enum(['APPROVE', 'REJECT']),
    note: z.string().trim().max(500).optional(),
});
/* ---------- Мониторинг чатов логистов ---------- */
export const INGEST_STATUSES = [
    'NEW',
    'PARSED',
    'DUPLICATE',
    'SKIPPED',
    'IMPORTED',
    'ERROR',
];
export const INGEST_STATUS_LABEL = {
    NEW: 'Новый',
    PARSED: 'Разобран',
    DUPLICATE: 'Дубль',
    SKIPPED: 'Пропущен',
    IMPORTED: 'Заявка создана',
    ERROR: 'Ошибка разбора',
};
export const SOURCE_VIA = ['BOT', 'USERBOT'];
export const SOURCE_VIA_LABEL = {
    BOT: 'Через бота',
    USERBOT: 'Через аккаунт',
};
export const addSourceChatSchema = z.object({
    /** `-100123…`, `@username` канала либо ссылка-приглашение `t.me/…`. */
    chatId: z.string().trim().min(2).max(120),
    /** Через бота (по умолчанию) или через аккаунт-читатель. */
    via: z.enum(SOURCE_VIA).default('BOT'),
});
/* ---------- Userbot: аккаунт-читатель чужих чатов ---------- */
export const USERBOT_STATUSES = [
    'OFFLINE',
    'CONNECTING',
    'NEED_CODE',
    'NEED_PASSWORD',
    'ONLINE',
    'ERROR',
];
export const USERBOT_STATUS_LABEL = {
    OFFLINE: 'Не подключён',
    CONNECTING: 'Подключается…',
    NEED_CODE: 'Ждёт код из Telegram',
    NEED_PASSWORD: 'Ждёт пароль (2FA)',
    ONLINE: 'На связи',
    ERROR: 'Ошибка',
};
export const userbotCredsSchema = z.object({
    apiId: z.string().trim().regex(/^\d{4,12}$/, 'API ID — только цифры'),
    apiHash: z.string().trim().regex(/^[a-f0-9]{32}$/i, 'API Hash — 32 hex-символа'),
});
export const userbotPhoneSchema = z.object({
    phone: z.string().trim().regex(/^\+?\d{8,15}$/, 'Телефон в формате +7999…'),
});
export const userbotCodeSchema = z.object({
    code: z.string().trim().regex(/^\d{4,7}$/, 'Код из 5–6 цифр'),
});
export const userbotPasswordSchema = z.object({
    password: z.string().min(1).max(256),
});
export const sourceChatPatchSchema = z.object({
    enabled: z.boolean().optional(),
    autoImport: z.boolean().optional(),
});
export const ingestActionSchema = z.object({
    /** Какой из разборов поста импортировать (по умолчанию первый). */
    index: z.number().int().min(0).max(50).optional(),
});
export const cargoFeedQuerySchema = z.object({
    transportMode: z.enum(TRANSPORT_MODES).optional(),
    fromRegion: z.string().optional(),
    toRegion: z.string().optional(),
    bodyType: bodyType.optional(),
    minPrice: z.coerce.number().int().nonnegative().optional(),
    minPricePerKm: z.coerce.number().int().nonnegative().optional(),
    minWeightKg: z.coerce.number().int().positive().optional(),
    maxWeightKg: z.coerce.number().int().positive().optional(),
    /** Только заявки от прямых грузовладельцев, без посредников. */
    directOnly: z.coerce.boolean().optional(),
    /** Кран должен поднять не меньше, метры вылета — не меньше. */
    minCraneCapacityKg: z.coerce.number().int().positive().optional(),
    minCraneReachM: z.coerce.number().positive().optional(),
    sort: z.enum(CARGO_SORTS).default('new'),
    /** Радиус вокруг точки водителя, км. Требует nearLat/nearLng. */
    radiusKm: z.coerce.number().positive().max(2000).optional(),
    nearLat: z.coerce.number().min(-90).max(90).optional(),
    nearLng: z.coerce.number().min(-180).max(180).optional(),
    /** Только догрузы (`1`) или только полные загрузки (`0`). */
    partial: z.coerce.boolean().optional(),
    /**
     * Подбор по пути.
     *
     * Задаются концы маршрута водителя; сервер оставляет заявки, у которых
     * и погрузка, и выгрузка лежат в коридоре вдоль этого маршрута, и считает
     * крюк. Без координат фильтр игнорируется — по названиям регионов
     * «по пути» не посчитать.
     */
    routeFromLat: z.coerce.number().min(-90).max(90).optional(),
    routeFromLng: z.coerce.number().min(-180).max(180).optional(),
    routeToLat: z.coerce.number().min(-90).max(90).optional(),
    routeToLng: z.coerce.number().min(-180).max(180).optional(),
    /** Ширина коридора и допустимый крюк, км. */
    detourKm: z.coerce.number().int().min(0).max(500).optional(),
    /** Свободный остаток в машине: тяжелее — не предлагать. */
    freeKg: z.coerce.number().int().positive().max(60_000).optional(),
    freeLengthM: z.coerce.number().positive().max(20).optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const createOfferSchema = z.object({
    /*
     * Ставка перевозчика — в валюте заявки. Своей валюты у отклика нет
     * намеренно: торг в двух валютах через плавающий курс — это спор о
     * курсе, а не о цене, и площадка не должна его создавать.
     */
    price: z.number().int().positive().max(100_000_000_000),
    vehicleId: z.string().optional(),
    comment: z.string().max(500).optional(),
});
export const createVehicleSchema = z.object({
    /** Вид техники. По умолчанию машина: прицепы заводят реже. */
    kind: z.enum(VEHICLE_KINDS).default('TRUCK'),
    /** Марка и модель из справочника, год и цвет — см. `trucks.ts`. */
    make: z.string().max(40).optional(),
    model: z.string().max(60).optional(),
    year: z.number().int().min(1990).max(2100).optional(),
    color: z.string().max(30).optional(),
    plate: z.string().min(4).max(15),
    bodyType,
    capacityKg: z.number().int().positive().max(60_000),
    volumeM3: z.number().positive().max(200).optional(),
    adr: z.boolean().default(false),
    craneCapacityKg: z.number().int().positive().max(50_000).nullable().optional(),
    craneReachM: z.number().positive().max(60).nullable().optional(),
    /**
     * Габариты и полная масса гружёной машины — необязательные, нужны только
     * для точной грузовой маршрутизации на карте (объезд мостов по высоте,
     * дорог с ограничением массы). Пусто — маршрут строится без учёта именно
     * этой машины.
     */
    heightM: z.number().positive().max(6).optional(),
    widthM: z.number().positive().max(4).optional(),
    lengthM: z.number().positive().max(30).optional(),
    grossWeightKg: z.number().int().positive().max(120_000).optional(),
    /** Записать технику на компанию, а не на себя. Проверяется по месту. */
    companyId: z.string().nullable().optional(),
});
/**
 * Правка единицы техники: закрепление водителя и сцепка.
 *
 * `null` здесь значимо и отличается от «поля нет»: снять закрепление —
 * это `driverId: null`, а не отсутствие ключа.
 */
export const patchVehicleSchema = z.object({
    /** Полный список закреплённых водителей. Пустой — снять всех. */
    crew: z.array(z.string()).max(2, 'Больше двух за одной машиной не закрепляют').optional(),
    /** Объявленный размер экипажа: один водитель или пара на дальние рейсы. */
    crewSize: z.number().int().min(1).max(2).optional(),
    hitchedToId: z.string().nullable().optional(),
    plate: z.string().min(4).max(15).optional(),
    make: z.string().max(40).nullable().optional(),
    model: z.string().max(60).nullable().optional(),
    year: z.number().int().min(1990).max(2100).nullable().optional(),
    color: z.string().max(30).nullable().optional(),
    capacityKg: z.number().int().positive().max(60_000).optional(),
    volumeM3: z.number().positive().max(200).nullable().optional(),
    adr: z.boolean().optional(),
    active: z.boolean().optional(),
    heightM: z.number().positive().max(6).nullable().optional(),
    widthM: z.number().positive().max(4).nullable().optional(),
    lengthM: z.number().positive().max(30).nullable().optional(),
    grossWeightKg: z.number().int().positive().max(120_000).nullable().optional(),
});
/**
 * Перегруз: передать рейс другой машине.
 *
 * Причина обязательна. Смена исполнителя в пути — событие, которое потом
 * разбирают: заказчик видит другую машину на выгрузке и спрашивает, почему.
 */
export const reassignOrderSchema = z
    .object({
    driverId: z.string().optional(),
    vehicleId: z.string().nullable().optional(),
    // Сообщение задано и на required: без него пропущенное поле отдаёт
    // англоязычное «Required» прямо в русский интерфейс.
    reason: z
        .string({ required_error: 'Опишите причину перегруза' })
        .min(3, 'Опишите причину')
        .max(300),
})
    .refine((v) => v.driverId || v.vehicleId !== undefined, {
    message: 'Укажите, кому или на какую машину передаём',
});
export const createSubscriptionSchema = z
    .object({
    fromRegion: z.string().max(80).optional(),
    toRegion: z.string().max(80).optional(),
    bodyTypes: z.array(bodyType).default([]),
    minPrice: z.number().int().nonnegative().optional(),
})
    .refine((v) => v.fromRegion || v.toRegion || v.bodyTypes.length > 0, {
    message: 'Задайте хотя бы один фильтр, иначе придут все грузы подряд',
});
export const updateOrderStatusSchema = z.object({
    status: z.enum(ORDER_STATUSES),
});
export const trackPointSchema = z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    speedKmh: z.number().nonnegative().max(200).optional(),
});
export const createReviewSchema = z.object({
    rating: z.number().int().min(1).max(5),
    text: z.string().max(1000).optional(),
});
export const updateProfileSchema = z.object({
    role: z.enum(ROLES).optional(),
    entityType: z.enum(ENTITY_TYPES).optional(),
    phone: z
        .string()
        .regex(/^\+?[0-9]{10,15}$/, 'Некорректный номер')
        .optional(),
    inn: z
        .string()
        .regex(/^([0-9]{10}|[0-9]{12})$/, 'ИНН — 10 или 12 цифр')
        .optional(),
    /** Кузова в быстрых кнопках над лентой. Пустой массив — сбросить набор. */
    favoriteBodyTypes: z.array(bodyType).max(12).optional(),
    feedLayout: z.enum(FEED_LAYOUTS).optional(),
    /** Пустая строка — «показывать в валюте самой сделки». */
    displayCurrency: z.union([z.enum(CURRENCIES), z.literal('')]).optional(),
});
/* ---------- Аутентификация ---------- */
export const AUTH_METHODS = ['password', 'telegram', 'email', 'phone'];
export const AUTH_METHOD_LABEL = {
    password: 'Логин и пароль',
    telegram: 'Telegram',
    email: 'Почта',
    phone: 'Телефон',
};
const loginField = z
    .string()
    .min(3, 'Логин от 3 символов')
    .max(64)
    .regex(/^[a-zA-Z0-9._-]+$/, 'Логин: латиница, цифры, точка, дефис и подчёркивание')
    // Логины сравниваем без учёта регистра, иначе Ivanov и ivanov —
    // два разных аккаунта, и это первый способ выдать себя за другого.
    .transform((v) => v.toLowerCase());
const passwordField = z
    .string()
    .min(8, 'Пароль от 8 символов')
    .max(128, 'Пароль слишком длинный');
export const loginSchema = z.object({
    login: loginField,
    password: z.string().min(1, 'Введите пароль'),
});
export const registerSchema = z.object({
    login: loginField,
    password: passwordField,
    firstName: z.string().min(2, 'Укажите имя').max(60),
    lastName: z.string().max(60).optional(),
    phone: z
        .string()
        .regex(/^\+?[0-9]{10,15}$/, 'Некорректный номер')
        .optional(),
    email: z.string().email('Некорректная почта').optional(),
});
export const emailLoginSchema = z.object({
    email: z.string().email('Некорректная почта'),
    password: z.string().min(1, 'Введите пароль'),
});
export const phoneLoginSchema = z.object({
    phone: z.string().regex(/^\+?[0-9]{10,15}$/, 'Некорректный номер'),
    password: z.string().min(1, 'Введите пароль'),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: passwordField,
});
/** Привязка логина к аккаунту, созданному через Telegram. */
export const setCredentialsSchema = z.object({
    login: loginField,
    password: passwordField,
});
/* ---------- Администрирование ---------- */
/**
 * Пароль, назначенный администратором.
 *
 * Отдельно от сброса на случайный: сброс нужен, когда человек забыл пароль,
 * а это — когда его заводят вручную или помогают войти по телефону. Логин
 * можно задать здесь же: без него пароль бесполезен, и заставлять
 * администратора делать два действия подряд незачем.
 */
export const adminSetPasswordSchema = z.object({
    password: passwordField,
    login: loginField.optional(),
});
export const adminUpdateUserSchema = z.object({
    banned: z.boolean().optional(),
    banReason: z.string().max(300).optional(),
    /** Допуск на площадку: активирует после модерации или деактивирует. */
    active: z.boolean().optional(),
    isAdmin: z.boolean().optional(),
    /** Должность в команде площадки. Снимается тем же полем — `NONE`. */
    staffRole: z.enum(STAFF_ROLES).optional(),
    role: z.enum(ROLES).optional(),
    /// Профиль деятельности правит только администратор — по обращению
    /// пользователя, а не самостоятельно из интерфейса.
    profileType: z.enum(PROFILE_TYPES).optional(),
    entityType: z.enum(ENTITY_TYPES).optional(),
});
/** Контакты и способы входа. Пустая строка означает «отвязать». */
export const adminUpdateContactsSchema = z.object({
    login: z
        .string()
        .regex(/^[a-zA-Z0-9._-]{3,64}$/, 'Логин: латиница, цифры, точка, дефис, подчёркивание')
        .transform((v) => v.toLowerCase())
        .or(z.literal(''))
        .optional(),
    email: z.string().email('Некорректная почта').or(z.literal('')).optional(),
    phone: z
        .string()
        .regex(/^\+?[0-9]{10,15}$/, 'Некорректный номер')
        .or(z.literal(''))
        .optional(),
    tgUsername: z
        .string()
        .regex(/^[a-zA-Z0-9_]{4,32}$/, 'Некорректный ник Telegram')
        .or(z.literal(''))
        .optional(),
});
export const adminUpdateCompanySchema = z.object({
    verified: z.boolean().optional(),
    isDirectShipper: z.boolean().optional(),
});
/* ---------- Ручная проверка участников ---------- */
/**
 * Состояние проверки. Отдельно от блокировки: заблокированный — наказанный,
 * непроверенный — ещё не рассмотренный. Смешаешь их — и новичок с первого
 * дня выглядит нарушителем.
 */
export const VERIFY_STATES = ['NONE', 'PENDING', 'APPROVED', 'REJECTED'];
export const VERIFY_STATE_LABEL = {
    NONE: 'Не проверен',
    PENDING: 'На проверке',
    APPROVED: 'Проверен',
    REJECTED: 'Отказано',
};
export const verifyDecisionSchema = z.object({
    state: z.enum(['APPROVED', 'REJECTED', 'PENDING', 'NONE']),
    /** Причина отказа. Обязательна: «отказано» без объяснения — это тупик. */
    note: z.string().max(500).optional(),
});
/**
 * Фильтры списка участников.
 *
 * Набор повторяет ленту грузов не для красоты: логика «чипы сверху, поиск
 * рядом, остальное в панели» уже знакома по «Найти груз», и второй раз
 * ей учиться не приходится.
 */
export const USER_SORTS = ['new', 'active', 'deals', 'rating', 'name'];
export const USER_SORT_LABEL = {
    new: 'Новые',
    active: 'Недавно заходили',
    deals: 'Больше сделок',
    rating: 'По рейтингу',
    name: 'По имени',
};
/**
 * `z.coerce.boolean()` не годится для булевых query-параметров:
 * `Boolean("0")` в JS — `true` (это непустая строка), поэтому `?x=0`
 * заворачивалось в `true` наравне с `?x=1`, и фильтр «только не X» был
 * физически недостижим никаким значением параметра. Разбираем сами —
 * тот же приём, что и для булевых переменных окружения в `env.ts`.
 */
const boolFromQuery = z.enum(['1', '0', 'true', 'false']).transform((v) => v === '1' || v === 'true');
export const adminUserQuerySchema = z.object({
    /** Поиск по имени, логину, телефону, почте, @username и ИНН компании. */
    q: z.string().max(120).optional(),
    role: z.enum(ROLES).optional(),
    profileType: z.enum(PROFILE_TYPES).optional(),
    entityType: z.enum(ENTITY_TYPES).optional(),
    staffRole: z.enum(STAFF_ROLES).optional(),
    verifyState: z.enum(VERIFY_STATES).optional(),
    /** `1` — только заблокированные, `0` — только незаблокированные. */
    banned: boolFromQuery.optional(),
    /** `1` — только допущенные, `0` — только ждущие модерации/деактивированные. */
    active: boolFromQuery.optional(),
    /** Только участники с привязанной компанией. */
    withCompany: boolFromQuery.optional(),
    /** Заходил за последние N дней. */
    activeDays: z.coerce.number().int().min(1).max(365).optional(),
    sort: z.enum(USER_SORTS).default('new'),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(40),
});
/* ---------- Рассылки ---------- */
export const NOTIFY_CHANNELS = ['TELEGRAM', 'EMAIL', 'SMS'];
export const NOTIFY_CHANNEL_LABEL = {
    TELEGRAM: 'Telegram',
    EMAIL: 'Почта',
    SMS: 'SMS',
};
export const NOTIFY_CHANNEL_HINT = {
    TELEGRAM: 'Бесплатно и мгновенно. Работает, если аккаунт привязан к боту.',
    EMAIL: 'Для документов и длинных сообщений. Нужен настроенный SMTP.',
    SMS: 'Дорого, поэтому только для срочного. Нужен шлюз и согласие человека.',
};
export const notifyPrefsSchema = z.object({
    notifyTelegram: z.boolean().optional(),
    notifyEmail: z.boolean().optional(),
    notifySms: z.boolean().optional(),
});
export const BROADCAST_TARGETS = ['USERS', 'CHAT'];
export const BROADCAST_TARGET_LABEL = {
    USERS: 'Участникам в личные сообщения',
    CHAT: 'В группу или канал',
};
/**
 * Сколько времени Telegram даёт убрать или поправить своё сообщение.
 *
 * Ограничение Bot API, а не наше решение: примерно двое суток. После
 * этого запись об отправленном остаётся, а сообщение у получателя — нет.
 * Число вынесено сюда, потому что от него зависит и подпись в форме, и
 * то, показывать ли кнопку отзыва вообще.
 */
export const RECALL_WINDOW_HOURS = 48;
/** Ещё можно отозвать? */
export function canRecall(sentAt, now = new Date()) {
    const at = typeof sentAt === 'string' ? new Date(sentAt) : sentAt;
    return now.getTime() - at.getTime() < RECALL_WINDOW_HOURS * 3600_000;
}
export const broadcastSchema = z.object({
    channels: z.array(z.enum(NOTIFY_CHANNELS)).min(1),
    /** Куда: участникам лично или в один чат целиком. */
    target: z.enum(BROADCAST_TARGETS).default('USERS'),
    /** Идентификатор группы или канала. Обязателен при target = CHAT. */
    chatId: z.string().trim().max(64).optional(),
    subject: z.string().max(120).optional(),
    body: z.string().min(1).max(4000),
    /** Кому: тот же набор фильтров, что и в списке участников. */
    audience: z
        .object({
        role: z.enum(ROLES).optional(),
        profileType: z.enum(PROFILE_TYPES).optional(),
        verifyState: z.enum(VERIFY_STATES).optional(),
        activeDays: z.coerce.number().int().min(1).max(365).optional(),
        withCompany: z.boolean().optional(),
        /** Поимённо: когда нужно написать двоим, а не сегменту. */
        userIds: z.array(z.string()).max(500).optional(),
    })
        .default({}),
    /** Прогон без отправки: показать, скольким уйдёт. */
    dryRun: z.boolean().default(false),
})
    .refine((v) => v.target !== 'CHAT' || Boolean(v.chatId), {
    message: 'Укажите группу или канал',
    path: ['chatId'],
});
export const broadcastEditSchema = z.object({
    subject: z.string().max(120).optional(),
    body: z.string().min(1).max(4000),
});
/* ---------- Диспетчерская: чужие рейсы для админа и поддержки ---------- */
export const dispatchQuerySchema = z.object({
    /** Номер заявки, груз, маршрут, имя или логин любой из сторон, госномер. */
    q: z.string().max(120).optional(),
    status: z.enum(ORDER_STATUSES).optional(),
    /** `1` — только незавершённые. По умолчанию именно они и нужны. */
    activeOnly: z.coerce.boolean().optional(),
    /** Рейсы, где вторая сторона молчит дольше N часов. */
    stalledHours: z.coerce.number().int().min(1).max(720).optional(),
    fromRegion: z.string().max(80).optional(),
    toRegion: z.string().max(80).optional(),
    companyId: z.string().optional(),
    driverId: z.string().optional(),
    logistId: z.string().optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
});
/* ---------- Журнал действий ---------- */
export const ACTION_TARGETS = [
    'USER', 'CARGO', 'ORDER', 'OFFER', 'COMPANY',
    'FILE', 'GROUP', 'CONTRACT', 'VEHICLE', 'SETTINGS',
];
export const ACTION_TARGET_LABEL = {
    USER: 'Участник',
    CARGO: 'Заявка',
    ORDER: 'Рейс',
    OFFER: 'Отклик',
    COMPANY: 'Компания',
    FILE: 'Файл',
    GROUP: 'Группа',
    CONTRACT: 'Контракт',
    VEHICLE: 'Машина',
    SETTINGS: 'Настройки',
};
/**
 * Человеческие названия действий.
 *
 * Точка вида `cargo.update` — то, по чему ищут и фильтруют; в списке
 * человек читает подпись. Неизвестный ключ показывается как есть: журнал
 * старше этого справочника, и прятать запись из-за отсутствия подписи
 * было бы хуже, чем показать техническое имя.
 */
export const ACTION_LABEL = {
    'cargo.create': 'Заявка опубликована',
    'cargo.update': 'Заявка изменена',
    'cargo.cancel': 'Заявка снята',
    'cargo.moderate.approve': 'Заявка пропущена модерацией',
    'cargo.moderate.reject': 'Заявка отклонена модерацией',
    'ingest.chat.add': 'Чат добавлен в мониторинг',
    'ingest.chat.update': 'Настройки чата мониторинга изменены',
    'ingest.chat.remove': 'Чат убран из мониторинга',
    'ingest.import': 'Из поста создана заявка',
    'ingest.skip': 'Пост мониторинга пропущен',
    'userbot.creds': 'Заданы ключи userbot',
    'userbot.login': 'Запрошен вход userbot',
    'userbot.logout': 'Выход userbot',
    // Снятие с причиной и удаление без неё — разные действия и в журнале.
    'cargo.block': 'Заявка снята с причиной (админ)',
    'cargo.delete': 'Заявка удалена (админ)',
    'offer.create': 'Отклик отправлен',
    'offer.accept': 'Отклик принят — сделка',
    'order.status': 'Статус рейса',
    'order.cancel': 'Рейс отменён',
    'order.assign': 'Смена водителя',
    'order.confirm': 'Доставка подтверждена',
    'file.upload': 'Файл приложен',
    'file.delete': 'Файл удалён',
    'auth.login': 'Вход',
    'auth.password.change': 'Смена пароля',
    'company.claim': 'Стал владельцем компании',
    'company.join': 'Вошёл в компанию по коду',
    'company.member.rights': 'Права сотрудника',
    'company.member.update': 'Место в компании (админ)',
    'company.member.remove': 'Место снято (админ)',
    'company.owner.replace': 'Смена владельца (админ)',
    'company.update': 'Компания изменена (админ)',
    'user.update': 'Участник изменён (админ)',
    'user.delete': 'Участник удалён (админ)',
    'user.set_password': 'Пароль задан админом',
    'user.reset_password': 'Пароль сброшен админом',
    'user.contacts': 'Контакты изменены (админ)',
    'settings.update': 'Настройки площадки',
    'feature.rule': 'Правило функции',
    'group.create': 'Группа создана',
    'group.update': 'Группа изменена',
    'group.delete': 'Группа удалена',
    'group.members.add': 'Участник добавлен в группу',
    'group.members.remove': 'Участник убран из группы',
    'contract.create': 'Контракт создан',
    'contract.update': 'Контракт изменён',
    'contract.delete': 'Контракт удалён',
    'contract.members.add': 'Участник добавлен в контракт',
    'contract.members.remove': 'Участник убран из контракта',
    'contract.override.add': 'Переопределение наценки добавлено',
    'contract.override.remove': 'Переопределение наценки убрано',
    'level.create': 'Уровень контрактов создан',
    'level.update': 'Уровень контрактов изменён',
    'level.delete': 'Уровень контрактов удалён',
    'currency.update': 'Валюта настроена',
    // Значение не пишется в журнал никогда — только имя параметра и факт правки.
    'secret.update': 'Ключ или токен изменён',
    'faq.create': 'Вопрос добавлен',
    'faq.update': 'Вопрос изменён',
    'faq.delete': 'Вопрос убран',
    'faq.restore': 'Вопрос возвращён',
    'broadcast.send': 'Рассылка отправлена',
    'broadcast.edit': 'Рассылка исправлена',
    'broadcast.recall': 'Рассылка отозвана',
    'auction.close': 'Торги завершены',
};
export const actionLogQuerySchema = z.object({
    actorId: z.string().optional(),
    targetId: z.string().optional(),
    targetType: z.enum(ACTION_TARGETS).optional(),
    /** Точное имя действия либо префикс через точку: `cargo` даст все cargo.*. */
    action: z.string().max(60).optional(),
    /** Только действия администраторов — при разборе жалобы на модерацию. */
    adminOnly: boolFromQuery.optional(),
    since: z.coerce.date().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    cursor: z.string().optional(),
});
/** Доля отмен в процентах. null, пока не было ни одной завершённой сделки. */
export function cancelRate(done, cancelled) {
    const total = done + cancelled;
    return total === 0 ? null : Math.round((cancelled / total) * 100);
}
export const SIMILAR_TITLE = {
    route: 'Ещё по этому направлению',
    region: 'Другие заявки из этого региона',
    fresh: 'Свежие заявки',
};
/* ---------- Группы ---------- */
export const GROUP_KINDS = ['RESTRICTION', 'PRIVILEGE'];
export const GROUP_KIND_LABEL = {
    RESTRICTION: 'Ограничение',
    PRIVILEGE: 'Привилегия',
};
export const GROUP_KIND_HINT = {
    RESTRICTION: 'Сужает ленту участникам: перечисленные направления они не увидят.',
    PRIVILEGE: 'Открывает закрытые грузы, которые остальным не показываются.',
};
const regionList = z.array(z.string().min(2).max(80)).max(120).default([]);
export const createGroupSchema = z.object({
    name: z.string().min(2).max(80),
    kind: z.enum(GROUP_KINDS),
    description: z.string().max(500).optional(),
    blockedFromRegions: regionList,
    blockedToRegions: regionList,
    allowedFromRegions: regionList,
    allowedToRegions: regionList,
    priority: z.number().int().min(0).max(1000).default(0),
});
export const updateGroupSchema = createGroupSchema.partial().extend({
    active: z.boolean().optional(),
});
/* ---------- Контракты ---------- */
export const CONTRACT_TYPES = ['BUYER', 'SELLER'];
export const CONTRACT_TYPE_LABEL = {
    BUYER: 'Покупатель',
    SELLER: 'Продавец',
};
export const CONTRACT_TYPE_HINT = {
    BUYER: 'Тарифицирует заказчика перевозки: наценка прибавляется к его счёту.',
    SELLER: 'Тарифицирует перевозчика: наценка удерживается из его ставки.',
};
export const OVERRIDE_SCOPES = ['BODY_TYPE', 'ROUTE', 'PARTNER'];
export const OVERRIDE_SCOPE_LABEL = {
    BODY_TYPE: 'Тип кузова',
    ROUTE: 'Направление',
    PARTNER: 'Контрагент',
};
/** Чем уже область, тем выше приоритет. Совпало несколько — берётся верхний. */
export const OVERRIDE_PRIORITY = {
    PARTNER: 3,
    ROUTE: 2,
    BODY_TYPE: 1,
};
export const NO_MARKUP = {
    contractId: null,
    contractName: null,
    totalPercent: 0,
    percent: 0,
    marketDegree: 0,
    usdt: 0,
    overrideId: null,
    overrideScope: null,
    percentPartRub: 0,
    weightPartRub: 0,
    unitPartRub: 0,
    feeRub: 0,
};
/** Итог наценки — сумма трёх слагаемых. Минус задаётся отрицательным числом. */
export function totalMarkup(m) {
    // Складываем в сотых долях процента: 1.5 + 0.4 в двоичных дробях
    // даёт 1.9000000000000001, и в интерфейсе это видно.
    return Math.round((m.percent + m.marketDegree + m.usdt) * 100) / 100;
}
/** Подходит ли переопределение под сделку. */
export function overrideMatches(o, d) {
    if (o.scope === 'PARTNER')
        return Boolean(o.partnerId) && o.partnerId === d.partnerId;
    if (o.scope === 'ROUTE') {
        return ((!o.fromRegion || o.fromRegion === d.fromRegion) &&
            (!o.toRegion || o.toRegion === d.toRegion) &&
            Boolean(o.fromRegion || o.toRegion));
    }
    return Boolean(o.bodyType) && d.bodyTypes.includes(o.bodyType);
}
/** Самое узкое подходящее переопределение. */
export function pickOverride(overrides, d) {
    const matched = overrides.filter((o) => overrideMatches(o, d));
    if (matched.length === 0)
        return null;
    return matched.reduce((best, o) => OVERRIDE_PRIORITY[o.scope] > OVERRIDE_PRIORITY[best.scope] ? o : best);
}
/**
 * Считает наценку по контракту.
 *
 * Процент берётся из переопределения, если оно нашлось, а рыночная степень
 * и USDT всегда остаются от контракта — так устроен исходный расчёт:
 * переопределение правит только базовую ставку, а не всю формулу.
 *
 * Общая для сервера и интерфейса: предпросмотр в форме и реальная сделка
 * не должны разъезжаться.
 */
export function computeMarkup(contract, deal, rates = {}) {
    if (!contract)
        return NO_MARKUP;
    const applied = pickOverride(contract.overrides, deal);
    const percent = applied ? applied.percent : contract.percent;
    const perWeight = applied?.perWeight ?? contract.perWeight;
    const perUnit = applied?.perUnit ?? contract.perUnit;
    const totalPercent = totalMarkup({
        percent,
        marketDegree: contract.marketDegree,
        usdt: contract.usdt,
    });
    const percentPartRub = (deal.priceRub * totalPercent) / 100;
    /*
     * Плоские надбавки — в своей валюте, и до сентября 2026 это молча
     * игнорировалось: «5 USD за тонну» удерживалось как 5 ₽ за тонну, с
     * обеих сторон сделки и с валютой USD по умолчанию у нового контракта.
     *
     * Курса нет — надбавка не берётся вовсе (`toRub` вернёт null). Взять её
     * как есть значило бы повторить ту же ошибку, а взять ноль — тихо
     * подарить участнику деньги площадки. Оба варианта хуже честного нуля
     * с видимым в журнале контрактом.
     */
    /*
     * Переводим всю надбавку разом, а не ставку за тонну.
     *
     * `toRub` округляет до целого рубля, и округление ставки до умножения
     * даёт смещение, растущее с весом: 5 $ за тонну при курсе 86,753 — это
     * 434 ₽ вместо 433,765, и на двадцати тоннах уже 8 680 против 8 675, а
     * на ста — двадцать пять рублей мимо. Всегда в одну сторону.
     */
    const weightPartRub = toRub(perWeight * deal.weightTons, contract.perWeightCurrency, rates) ?? 0;
    const unitPartRub = toRub(perUnit, contract.perUnitCurrency, rates) ?? 0;
    const feeRub = Math.round((percentPartRub + weightPartRub + unitPartRub) * contract.coefficient);
    return {
        contractId: contract.id,
        contractName: contract.name,
        totalPercent,
        percent,
        marketDegree: contract.marketDegree,
        usdt: contract.usdt,
        overrideId: applied?.id ?? null,
        overrideScope: applied?.scope ?? null,
        percentPartRub: Math.round(percentPartRub),
        weightPartRub: Math.round(weightPartRub),
        unitPartRub: Math.round(unitPartRub),
        feeRub,
    };
}
export function computeDeal(priceRub, buyer, seller) {
    // Удержание не может увести перевозчика в минус, но и отрицательная
    // наценка допустима: так задаётся доплата или скидка.
    const sellerFee = Math.min(seller.feeRub, priceRub);
    return {
        priceRub,
        buyer,
        seller,
        driverPayoutRub: priceRub - sellerFee,
        shipperTotalRub: priceRub + buyer.feeRub,
        platformRub: sellerFee + buyer.feeRub,
    };
}
/* ---------- Схемы контрактов ---------- */
export const contractLevelSchema = z.object({
    code: z.string().min(1).max(32),
    name: z.string().min(2).max(80),
    description: z.string().max(300).optional(),
    sortOrder: z.number().int().min(0).max(1000).default(0),
    active: z.boolean().default(true),
});
export const contractSchema = z.object({
    code: z.string().max(32).nullable().optional(),
    name: z.string().min(2).max(80),
    description: z.string().max(300).nullable().optional(),
    type: z.enum(CONTRACT_TYPES),
    region: z.string().max(80).nullable().optional(),
    levelId: z.string().nullable().optional(),
    coefficient: z.number().min(0).max(100).default(1),
    isDeposit: z.boolean().default(false),
    comment: z.string().max(500).nullable().optional(),
    active: z.boolean().default(true),
    percent: z.number().min(-100).max(100).default(0),
    marketDegree: z.number().min(-100).max(100).default(0),
    usdt: z.number().min(-100).max(100).default(0),
    perWeight: z.number().min(-1_000_000).max(1_000_000).default(0),
    perWeightCurrency: z.enum(CURRENCIES).default('USD'),
    perUnit: z.number().min(-1_000_000).max(1_000_000).default(0),
    perUnitCurrency: z.enum(CURRENCIES).default('USD'),
});
export const contractUpdateSchema = contractSchema.partial();
export const contractOverrideSchema = z
    .object({
    scope: z.enum(OVERRIDE_SCOPES),
    bodyType: z.enum(BODY_TYPES).nullable().optional(),
    fromRegion: z.string().max(80).nullable().optional(),
    toRegion: z.string().max(80).nullable().optional(),
    partnerId: z.string().nullable().optional(),
    percent: z.number().min(-100).max(100),
    perWeight: z.number().min(-1_000_000).max(1_000_000).nullable().optional(),
    perUnit: z.number().min(-1_000_000).max(1_000_000).nullable().optional(),
})
    .refine((v) => v.scope !== 'BODY_TYPE' || Boolean(v.bodyType), { message: 'Выберите тип кузова', path: ['bodyType'] })
    .refine((v) => v.scope !== 'ROUTE' || Boolean(v.fromRegion || v.toRegion), { message: 'Укажите хотя бы один регион направления', path: ['fromRegion'] })
    .refine((v) => v.scope !== 'PARTNER' || Boolean(v.partnerId), {
    message: 'Выберите контрагента',
    path: ['partnerId'],
});
/**
 * Статус отклика по-русски.
 *
 * Название состояния из базы человеку не адресовано: «PENDING» в строке
 * «статус: PENDING» — это протекший в интерфейс код, а не сообщение.
 */
export const OFFER_STATUS_LABEL = {
    PENDING: 'на рассмотрении',
    ACCEPTED: 'принят',
    REJECTED: 'отклонён',
    WITHDRAWN: 'отозван',
};
/**
 * Каких компаний показывать в админке.
 *
 * Раньше был признак «только непроверенные», включённый по умолчанию:
 * администратор видел часть списка, не зная об этом. Состояний тут не
 * два, и они не отрицают друг друга — поэтому набор значений, а не
 * тумблер, и «все» первым.
 */
export const COMPANY_STATES = ['', 'pending', 'verified', 'noowner'];
export const COMPANY_STATE_LABEL = {
    '': 'Все',
    pending: 'На модерации',
    verified: 'Проверенные',
    noowner: 'Без владельца',
};
export const INTEGRATIONS = [
    {
        key: 'BOT_TOKEN',
        title: 'Токен бота',
        hint: 'Выдаётся @BotFather. Без него не работают команды бота, уведомления ' +
            'и вход через Telegram; веб-кабинет работает.',
        group: 'Telegram',
        secret: true,
        required: true,
        placeholder: '123456:AA…',
    },
    {
        key: 'TELEGRAM_CHANNEL',
        title: 'Канал для публикаций',
        hint: 'Через @ или числовой идентификатор. Бот должен быть администратором ' +
            'канала — это и проверяет кнопка «Проверить».',
        group: 'Telegram',
        secret: false,
        placeholder: '@my_channel',
    },
    {
        key: 'MINIAPP_URL',
        title: 'Адрес приложения',
        hint: 'Куда ведёт кнопка «Открыть биржу» в боте. Должен совпадать с адресом ' +
            'в @BotFather, иначе подпись входа из Telegram не сойдётся.',
        group: 'Telegram',
        secret: false,
        required: true,
        placeholder: 'https://birzha.example.ru',
    },
    {
        key: 'USERBOT_API_ID',
        title: 'API ID (userbot)',
        hint: 'С my.telegram.org (раздел «API development tools»). Нужен, чтобы ' +
            'читать чаты конкурентов от имени отдельного аккаунта — туда бота не ' +
            'добавить. Работает вместе с API Hash.',
        group: 'Telegram',
        secret: true,
        placeholder: '1234567',
    },
    {
        key: 'USERBOT_API_HASH',
        title: 'API Hash (userbot)',
        hint: 'Оттуда же, где API ID. 32 шестнадцатеричных символа.',
        group: 'Telegram',
        secret: true,
        placeholder: 'abcd…',
    },
    {
        key: 'USERBOT_SESSION',
        title: 'Сессия userbot',
        hint: 'Заполняется автоматически после входа в разделе «Импорт из чатов». ' +
            'Полный доступ к аккаунту-читателю — не показывать и не пересылать. ' +
            'Смена ключа шифрования секретов делает её нечитаемой, вход придётся ' +
            'повторить.',
        group: 'Telegram',
        secret: true,
        placeholder: '',
    },
    {
        key: 'DADATA_TOKEN',
        title: 'Токен DaData',
        hint: 'Подтягивает название и ОКВЭД компании по ИНН. Пусто — компания ' +
            'заводится с названием «ИНН …», проверить её будет нечем.',
        group: 'Справочники',
        secret: true,
    },
    {
        key: 'ORS_API_KEY',
        title: 'Ключ OpenRouteService',
        hint: 'Маршрут по дорогам и расстояние. Пусто — расстояние считается по ' +
            'прямой с коэффициентом, и цена за километр получается приблизительной.',
        group: 'Справочники',
        secret: true,
    },
    {
        key: 'SMTP_URL',
        title: 'Почтовый сервер',
        hint: 'Вида smtp://логин:пароль@хост:порт. Пусто — канал «почта» выключен.',
        group: 'Рассылки',
        secret: true,
        placeholder: 'smtp://user:pass@mail.example.ru:587',
    },
    {
        key: 'SMTP_FROM',
        title: 'Отправитель писем',
        hint: 'Адрес в поле «От кого».',
        group: 'Рассылки',
        secret: false,
        placeholder: 'Биржа <no-reply@example.ru>',
    },
    {
        key: 'SMS_API_KEY',
        title: 'Ключ SMS-шлюза',
        hint: 'Пусто — канал SMS выключен.',
        group: 'Рассылки',
        secret: true,
    },
    {
        key: 'SMS_SENDER',
        title: 'Имя отправителя SMS',
        hint: 'Подпись, которую увидит получатель.',
        group: 'Рассылки',
        secret: false,
    },
];
export const secretPatchSchema = z.object({
    key: z.string().min(1).max(64),
    /** Пустая строка — «убрать из настроек и вернуться к окружению». */
    value: z.string().max(500),
});
/* ---------- Уведомления ---------- */
export const NOTIFICATION_KINDS = [
    'OFFER_NEW',
    'OFFER_ACCEPTED',
    'OFFER_REJECTED',
    'ORDER_STATUS',
    'ORDER_CANCELLED',
    'ORDER_CONFIRMED',
    'ORDER_REASSIGNED',
    'CARGO_BLOCKED',
    'CARGO_UPDATED',
    'BID_OUTBID',
    'SYSTEM',
];
/** Тон уведомления: определяет цвет точки в ленте. */
export const NOTIFICATION_TONE = {
    OFFER_NEW: 'info',
    OFFER_ACCEPTED: 'success',
    OFFER_REJECTED: 'warn',
    ORDER_STATUS: 'info',
    ORDER_CANCELLED: 'danger',
    ORDER_CONFIRMED: 'success',
    ORDER_REASSIGNED: 'warn',
    CARGO_BLOCKED: 'danger',
    CARGO_UPDATED: 'warn',
    BID_OUTBID: 'warn',
    SYSTEM: 'info',
};
/* ---------- Функции площадки ---------- */
/**
 * Управляемые функции.
 *
 * Список закрытый и общий для сервера и клиента: строка-опечатка в правиле
 * иначе молча выключала бы несуществующую функцию.
 */
export const FEATURES = [
    'cargoPhotos',
    'driverDocs',
    'expenses',
    'moneyDashboard',
    'analytics',
    'companies',
    'truckBoard',
    'rateHint',
    'trackMap',
    'publicVehicleInfo',
];
export const FEATURE_LABEL = {
    cargoPhotos: 'Фото и документы к заявке',
    driverDocs: 'Документы перевозчика',
    expenses: 'Расходы по рейсу',
    moneyDashboard: 'Сводка по деньгам',
    analytics: 'Отчёты и выгрузки',
    companies: 'Компании и сотрудники',
    truckBoard: 'Доска свободных машин',
    publicVehicleInfo: 'Номера машин видны всем',
    rateHint: 'Подсказка ставки',
    trackMap: 'Карта с машиной',
};
export const FEATURE_HINT = {
    cargoPhotos: 'Снимок груза и накладные прямо в карточке заявки.',
    driverDocs: 'Права, СТС, страховка в профиле перевозчика.',
    expenses: 'Топливо, платные дороги и прочее внутри рейса.',
    moneyDashboard: 'Заработок, траты и чистыми за период.',
    analytics: 'Отчёты по сделкам с фильтрами, рейтинг участников и выгрузка в CSV.',
    companies: 'Диспетчер, водители, права и приглашения.',
    truckBoard: 'Объявления о свободных машинах.',
    rateHint: 'Медианная ставка ₽/км по завершённым рейсам.',
    trackMap: 'Положение машины на карте в рейсе.',
    publicVehicleInfo: 'Госномер и тип техники в откликах и на доске видны всем. Выключите — ' +
        'останутся видны только сторонам сделки: перевозчики просят не светить ' +
        'номера в открытой ленте.',
};
/** Значения по умолчанию, если правил в базе нет. */
export const FEATURE_DEFAULT = {
    cargoPhotos: true,
    driverDocs: true,
    expenses: true,
    moneyDashboard: true,
    analytics: true,
    companies: true,
    truckBoard: true,
    rateHint: true,
    trackMap: true,
    publicVehicleInfo: true,
};
export const FEATURE_SCOPES = ['GLOBAL', 'GROUP', 'USER'];
export const featureRuleSchema = z.object({
    feature: z.enum(FEATURES),
    scope: z.enum(FEATURE_SCOPES),
    enabled: z.boolean(),
    groupId: z.string().optional(),
    userId: z.string().optional(),
    comment: z.string().max(200).optional(),
});
/* ---------- Компании ---------- */
/**
 * Состояние единицы техники — то, за чем диспетчер приходит на экран парка.
 *
 * Три состояния, а не два: «свободна» и «в рейсе» очевидны, но выведенная
 * из парка машина — не то же самое, что свободная, и путать их нельзя.
 */
export const FLEET_STATES = ['free', 'busy', 'off'];
export const FLEET_STATE_LABEL = {
    free: 'свободна',
    busy: 'в рейсе',
    off: 'выведена',
};
export const COMPANY_ROLES = ['OWNER', 'MANAGER', 'DRIVER'];
export const COMPANY_ROLE_LABEL = {
    OWNER: 'Владелец',
    MANAGER: 'Диспетчер',
    DRIVER: 'Водитель',
};
export const COMPANY_ROLE_HINT = {
    OWNER: 'Может всё: сотрудники, права, документы, деньги.',
    MANAGER: 'Берёт грузы, назначает водителей, ведёт рейсы и документы.',
    DRIVER: 'Видит только назначенные ему рейсы и свои расходы.',
};
/** Кто может распоряжаться сотрудниками и рейсами компании. */
export const COMPANY_MANAGING_ROLES = ['OWNER', 'MANAGER'];
/** Распоряжается ли человек компанией. Роли — набор, достаточно одной. */
export function manages(roles) {
    return roles.some((r) => COMPANY_MANAGING_ROLES.includes(r));
}
/**
 * Главная роль — для подписи и сортировки.
 *
 * Показывать «Владелец · Диспетчер · Водитель» в списке из двадцати человек
 * нечитаемо; в строке остаётся старшая, полный набор виден в карточке.
 */
export function topRole(roles) {
    if (roles.includes('OWNER'))
        return 'OWNER';
    if (roles.includes('MANAGER'))
        return 'MANAGER';
    return 'DRIVER';
}
export const companyInviteSchema = z.object({
    role: z.enum(COMPANY_ROLES).default('DRIVER'),
    canSeeRates: z.boolean().default(false),
    canBook: z.boolean().default(false),
    /** Сколько часов живёт код. Сутки хватает, чтобы передать его водителю. */
    ttlHours: z.number().int().min(1).max(168).default(24),
});
/**
 * Заведение сотрудника владельцем компании.
 *
 * Отдельно от кода-приглашения: код требует, чтобы у человека уже был
 * аккаунт и Telegram. Наёмному водителю часто нет ни того, ни другого —
 * учётную запись заводит контора и отдаёт ему логин с паролем в руки.
 */
export const companyMemberCreateSchema = z.object({
    firstName: z.string().min(2, 'Укажите имя').max(60),
    lastName: z.string().max(60).optional(),
    // Телефон обязателен: по нему водителя ищут, когда он не отвечает
    // в приложении, и по нему же его находит заказчик.
    phone: z.string().regex(/^\+?[0-9]{10,15}$/, 'Некорректный номер'),
    email: z.string().email('Некорректная почта').optional(),
    login: z
        .string()
        .min(3, 'Логин от 3 символов')
        .max(32)
        .regex(/^[a-zA-Z0-9._-]+$/, 'Латиница, цифры, точка, дефис, подчёркивание'),
    /**
     * Telegram водителя. Нужен не для входа, а для слежения за машиной:
     * Mini App в фоне геопозицию отдавать не умеет, постоянную трансляцию
     * ведёт бот. Без него рейс виден только со слов водителя.
     */
    tgUsername: z
        .string()
        .regex(/^[a-zA-Z0-9_]{5,32}$/, 'Имя в Telegram без @')
        .optional(),
    /**
     * Кем работает. Набор, а не одна роль: в малой конторе владелец сам
     * садится за руль и сам же ведёт переписку с заказчиками.
     */
    roles: z.array(z.enum(COMPANY_ROLES)).min(1).default(['DRIVER']),
    /** Можно ли ставить за руль. У водителя подразумевается, у прочих — нет. */
    canDrive: z.boolean().optional(),
    canSeeRates: z.boolean().default(false),
    canBook: z.boolean().default(false),
});
export const companyMemberPatchSchema = z.object({
    /** Полный набор ролей. Пустой не принимаем: человек без роли — не сотрудник. */
    roles: z.array(z.enum(COMPANY_ROLES)).min(1, 'Оставьте хотя бы одну роль').optional(),
    canDrive: z.boolean().optional(),
    canSeeRates: z.boolean().optional(),
    canBook: z.boolean().optional(),
    active: z.boolean().optional(),
});
/**
 * Правка человека в компании администратором.
 *
 * Отличается от `companyMemberPatchSchema` тем, что допускает пустой
 * набор ролей — им администратор снимает место целиком у человека,
 * попавшего в компанию по ошибке.
 */
export const adminCompanyMemberSchema = z.object({
    roles: z.array(z.enum(COMPANY_ROLES)).optional(),
    canDrive: z.boolean().optional(),
    canSeeRates: z.boolean().optional(),
    canBook: z.boolean().optional(),
    active: z.boolean().optional(),
    /** Отвязать от компании: снимает `User.companyId`. */
    detach: z.boolean().optional(),
});
/* ---------- Расходы в рейсе ---------- */
export const EXPENSE_SIDES = ['CARRIER', 'SHIPPER'];
/**
 * Категории трат.
 *
 * Общий список на обе стороны: у заказчика свои статьи (упаковка,
 * страховка, простой), у перевозчика свои, но пересечение велико,
 * а два справочника разошлись бы уже на второй правке.
 */
export const EXPENSE_CATEGORIES = [
    'FUEL',
    'TOLL',
    'PARKING',
    'LOADING',
    'REPAIR',
    'FINE',
    'MEAL',
    'PACKING',
    'INSURANCE',
    'DEMURRAGE',
    'OTHER',
];
export const EXPENSE_CATEGORY_LABEL = {
    FUEL: 'Топливо',
    TOLL: 'Платные дороги',
    PARKING: 'Стоянка',
    LOADING: 'Погрузка и выгрузка',
    REPAIR: 'Ремонт в пути',
    FINE: 'Штраф',
    MEAL: 'Питание',
    PACKING: 'Упаковка и крепление',
    INSURANCE: 'Страхование груза',
    DEMURRAGE: 'Простой',
    OTHER: 'Прочее',
};
/** Статьи, которые предлагаются заказчику. */
export const SHIPPER_CATEGORIES = [
    'LOADING',
    'PACKING',
    'INSURANCE',
    'DEMURRAGE',
    'OTHER',
];
/** Статьи перевозчика. */
export const CARRIER_CATEGORIES = [
    'FUEL',
    'TOLL',
    'PARKING',
    'LOADING',
    'REPAIR',
    'FINE',
    'MEAL',
    'OTHER',
];
export const expenseSchema = z.object({
    category: z.enum(EXPENSE_CATEGORIES),
    side: z.enum(EXPENSE_SIDES).optional(),
    amountRub: z.number().int().positive().max(1_000_000),
    note: z.string().max(200).optional(),
    spentAt: z.coerce.date().optional(),
    fileId: z.string().optional(),
});
/* ---------- Аналитика и выгрузки ---------- */
/**
 * Область отчёта. Определяет не вид, а круг сделок, и потому проверяется
 * на сервере: подмена параметра не должна открывать чужую выручку.
 */
export const ANALYTICS_SCOPES = ['mine', 'company', 'platform'];
export const ANALYTICS_SCOPE_LABEL = {
    mine: 'Мои сделки',
    company: 'Компания',
    platform: 'Вся площадка',
};
/**
 * Разрезы отчёта.
 *
 * Набор взят из того, что реально спрашивают в диспетчерской: как идут дела
 * по дням, кто из водителей везёт, какие направления кормят. Произвольный
 * конструктор измерений здесь был бы дороже в поддержке, чем полезен.
 */
export const ANALYTICS_GROUPS = [
    'day',
    'month',
    'driver',
    'logist',
    'company',
    'route',
    'vehicle',
    'status',
    'cargo',
];
export const ANALYTICS_GROUP_LABEL = {
    day: 'По дням',
    month: 'По месяцам',
    driver: 'По исполнителям',
    logist: 'По логистам',
    company: 'По компаниям',
    route: 'По направлениям',
    vehicle: 'По машинам',
    status: 'По статусам',
    cargo: 'По заявкам',
};
export const analyticsQuerySchema = z.object({
    scope: z.enum(ANALYTICS_SCOPES).optional(),
    groupBy: z.enum(ANALYTICS_GROUPS).optional(),
    /** Границы периода включительно. Пусто — последние 90 дней. */
    from: z.string().optional(),
    to: z.string().optional(),
    status: z.enum(ORDER_STATUSES).optional(),
    driverId: z.string().optional(),
    logistId: z.string().optional(),
    companyId: z.string().optional(),
    vehicleId: z.string().optional(),
    cargoId: z.string().optional(),
});
/** Что участнику разрешено выгружать. Формы совпадают со сценариями CRM. */
export const EXPORT_KINDS = [
    'deals',
    'cargos',
    'expenses',
    'users',
    'companies',
    'vehicles',
];
export const EXPORT_KIND_LABEL = {
    deals: 'Сделки',
    cargos: 'Заявки',
    expenses: 'Расходы',
    users: 'Участники',
    companies: 'Компании',
    vehicles: 'Автопарк',
};
/* ---------- Файлы ---------- */
export const FILE_KINDS = [
    'DRIVER_DOC',
    'POD',
    'CARGO_PHOTO',
    'EXPENSE_RECEIPT',
    'ORDER_PDF',
    'VEHICLE_DOC',
];
export const FILE_KIND_LABEL = {
    DRIVER_DOC: 'Документ перевозчика',
    POD: 'Накладная',
    CARGO_PHOTO: 'Фото груза',
    EXPENSE_RECEIPT: 'Чек',
    ORDER_PDF: 'Документ по перевозке',
    VEHICLE_DOC: 'Документ на технику',
};
export const DRIVER_DOC_TYPES = [
    'LICENSE',
    'PASSPORT',
    'STS',
    'INSURANCE',
    'ADR',
    'OTHER',
];
export const DRIVER_DOC_LABEL = {
    LICENSE: 'Водительское удостоверение',
    PASSPORT: 'Паспорт',
    STS: 'СТС на машину',
    INSURANCE: 'Страховка',
    ADR: 'Допуск ADR',
    OTHER: 'Другое',
};
/* ---------- Подсказка ставки ---------- */
export const rateHintQuerySchema = z.object({
    fromRegion: z.string().min(2).max(80),
    toRegion: z.string().max(80).optional(),
    bodyType: z.enum(BODY_TYPES).optional(),
    distanceKm: z.coerce.number().int().positive().optional(),
});
/* ---------- Свободные машины ---------- */
export const availabilitySchema = z
    .object({
    vehicleId: z.string().nullable().optional(),
    fromRegion: z.string().min(2).max(80),
    toRegion: z.string().max(80).nullable().optional(),
    bodyType: z.enum(BODY_TYPES),
    capacityKg: z.number().int().positive().max(60_000),
    availableFrom: z.coerce.date(),
    availableTo: z.coerce.date(),
    note: z.string().max(300).nullable().optional(),
    /**
     * Координаты концов маршрута.
     *
     * Нужны только для догруза: подбор «по пути» считается по геометрии,
     * а по названиям регионов расстояние до маршрута не вычислить.
     */
    fromLat: z.number().min(-90).max(90).nullable().optional(),
    fromLng: z.number().min(-180).max(180).nullable().optional(),
    toLat: z.number().min(-90).max(90).nullable().optional(),
    toLng: z.number().min(-180).max(180).nullable().optional(),
    /**
     * Свободный остаток. Машина уже гружёная, и для догруза важно не то,
     * сколько она поднимает всего, а сколько в ней осталось.
     */
    freeKg: z.number().int().positive().max(60_000).nullable().optional(),
    freeM3: z.number().positive().max(200).nullable().optional(),
    freeLengthM: z.number().positive().max(20).nullable().optional(),
    /** Сколько километров крюка водитель готов сделать ради догруза. */
    detourKm: z.number().int().min(0).max(500).default(50),
})
    .refine((v) => v.availableTo >= v.availableFrom, {
    message: 'Дата окончания раньше начала',
    path: ['availableTo'],
});
const ETA_TABLE = [
    { upTo: 500, min: 1, max: 2 },
    { upTo: 1500, min: 2, max: 3 },
    { upTo: 3000, min: 3, max: 7 },
    { upTo: Infinity, min: 5, max: 14 },
];
function plural(n, one, few, many) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11)
        return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14))
        return few;
    return many;
}
export function deliveryEta(distanceKm) {
    if (!distanceKm || distanceKm <= 0)
        return null;
    const row = ETA_TABLE.find((r) => distanceKm <= r.upTo);
    return {
        minDays: row.min,
        maxDays: row.max,
        label: `${row.min}–${row.max} ${plural(row.max, 'день', 'дня', 'дней')}`,
    };
}
export const settingsSchema = z.object({
    supportTelegram: z
        .string()
        .regex(/^[a-zA-Z0-9_]{0,32}$/, 'Ник Telegram: латиница, цифры, подчёркивание')
        .transform((v) => v.replace(/^@/, ''))
        .optional(),
    supportPhone: z
        .string()
        .regex(/^\+?[0-9]{10,15}$/, 'Некорректный номер')
        .or(z.literal(''))
        .optional(),
    supportHours: z.string().max(80).optional(),
    supportNote: z.string().max(300).or(z.literal('')).optional(),
    faqEnabled: z.boolean().optional(),
    etaEnabled: z.boolean().optional(),
    // Верхняя граница — час: продление длиннее ломает не снайпера, а
    // планирование, ради которого срок и назначают.
    bidExtendSeconds: z.coerce.number().int().min(0).max(3600).optional(),
    botProxyEnabled: z.boolean().optional(),
    // Пустая строка разрешена: это «адрес не задан», а не ошибка ввода.
    botProxyUrl: z
        .string()
        .trim()
        .max(200)
        .refine((v) => v === '' || /^https?:\/\/[^\s]+$/.test(v), 'Адрес вида http://хост:порт')
        .optional(),
    registrationModerationEnabled: z.boolean().optional(),
    cargoModerationEnabled: z.boolean().optional(),
    transportModes: z.array(z.enum(TRANSPORT_MODES)).optional(),
});
export const faqSectionSchema = z.object({
    title: z.string().min(2).max(120),
    /** Пункты приходят строками; пустые отбрасываются на сервере. */
    items: z.array(z.string().max(500)).max(30),
    note: z.string().max(500).nullable().optional(),
    sortOrder: z.number().int().min(0).max(1000).default(0),
    active: z.boolean().default(true),
});
/**
 * Наполнение справки по умолчанию. Заводится в базу при первом старте,
 * дальше правится из админки и отсюда больше не читается.
 */
export const DEFAULT_FAQ = [
    {
        sortOrder: 10,
        active: true,
        title: 'Сроки доставки',
        items: [
            'До 500 км — 1–2 дня',
            '500–1500 км — 2–3 дня',
            '1500–3000 км — 3–7 дней',
            'Свыше 3000 км — 5–14 дней',
        ],
        note: 'Ориентир по расстоянию. Точный срок стороны согласуют при оформлении.',
    },
    {
        sortOrder: 20,
        active: true,
        title: 'Какие грузы возим',
        items: [
            'Стройматериалы, металл, оборудование, спецтехника',
            'Продукты питания — рефрижератор',
            'Одежда и бытовые товары',
            'Опасные грузы (ADR) и негабарит',
            'Домашние переезды',
        ],
        note: null,
    },
    {
        sortOrder: 30,
        active: true,
        title: 'Страхование',
        items: [
            'Груз застрахован, страховка входит в стоимость',
            'Допстрахование на полную стоимость — по запросу',
        ],
        note: null,
    },
    {
        sortOrder: 40,
        active: true,
        title: 'Документы',
        items: [
            'Заявка на перевозку — поможем заполнить',
            'Договор — подготовим',
            'Транспортная накладная',
            'Работаем с ЭДО, оригиналы — курьером или почтой',
        ],
        note: null,
    },
    {
        sortOrder: 50,
        active: true,
        title: 'Оплата',
        items: [
            'Безналичный расчёт, наличные, перевод на карту',
            'Возможна отсрочка платежа — указывается в заявке',
        ],
        note: null,
    },
    {
        sortOrder: 60,
        active: true,
        title: 'Отслеживание',
        items: [
            'Статус рейса виден обеим сторонам в реальном времени',
            'Положение машины на карте, пока водитель ведёт трансляцию',
            'История статусов с точным временем каждого шага',
        ],
        note: null,
    },
    {
        sortOrder: 70,
        active: true,
        title: 'Минимальный объём',
        items: [
            'От 1 тонны',
            'Меньше — сборные перевозки',
            'Негабарит — подберём подходящий транспорт',
        ],
        note: null,
    },
];
/* ---------- Пояснения к полям ---------- */
/** Подсказки у полей: короткие, по одному предложению. */
export const HINTS = {
    docs: 'Права, СТС, страховка. Заказчик увидит их только после того, как ' +
        'вы возьмёте у него рейс. Принимаем фото и PDF до 10 МБ.',
    bodyType: 'Тент — универсальный. Рефрижератор и изотерм — для продуктов. ' +
        'Трал — для спецтехники и негабарита.',
    weight: 'Минимум для отдельной машины — 1 тонна. Меньше — только сборным грузом.',
    delayDays: 'Через сколько дней после выгрузки будет оплата. Ноль — расчёт сразу. ' +
        'Чем больше отсрочка, тем меньше откликов.',
    vat: 'Ставка указана с НДС или без. Влияет на то, кто из перевозчиков возьмётся.',
    adr: 'Опасный груз: нужны допуск ADR у водителя и оборудованная машина.',
    manipulator: 'Кран-манипулятор грузит сам — техника и грузчики на месте не нужны. ' +
        'Укажите вес самого тяжёлого места и расстояние от машины до точки ' +
        'разгрузки: по ним перевозчик поймёт, хватит ли стрелы.',
    audience: 'Закрытая публикация: заявку увидят только участники выбранной группы, ' +
        'в общей ленте её не будет.',
    eta: 'Ориентир по расстоянию. Точный срок стороны согласуют при оформлении.',
    markup: 'Итог складывается из трёх слагаемых. Переопределения меняют только ' +
        'первое, остальные остаются от контракта.',
    entityType: 'Физлицу недоступны заявки с безналичной оплатой: заказчик-юрлицо ' +
        'не сможет легально провести платёж.',
    directShipper: 'Прямой грузовладелец везёт свой груз. Посредник перепродаёт заявку, ' +
        'и ставка по цепочке обычно ниже.',
};
/**
 * Тур по площадке — свой на каждый профиль деятельности.
 *
 * Разный не ради разнообразия: перевозчик и грузовладелец приходят за
 * противоположным. Показывать грузовладельцу, как откликаться на чужую
 * заявку, значит тратить его внимание на то, чего он делать не будет, —
 * а внимание в первом заходе конечно и тратится один раз.
 *
 * Тексты держатся в одном месте с остальными формулировками, чтобы
 * правка не требовала пересборки интерфейса.
 */
export const TOURS = {
    CARRIER: [
        {
            anchor: 'nav-feed',
            screen: 'feed',
            title: 'Лента грузов',
            text: 'Здесь появляются свободные заявки. Новые приходят сами — обновлять ' +
                'страницу не нужно, кто первый откликнулся, тот и везёт.',
        },
        {
            anchor: 'feed-filters',
            screen: 'feed',
            title: 'Отберите своё',
            text: 'Регион загрузки и выгрузки, тип кузова, ставка за километр. ' +
                'Сохранённые направления присылают push, когда появляется подходящий груз.',
        },
        {
            anchor: 'nav-trucks',
            title: 'Объявите свободную машину',
            text: 'Укажите, где и когда освобождаетесь, — заказчики ищут попутный ' +
                'груз под обратную загрузку. Это то, что снижает порожний пробег.',
        },
        {
            anchor: 'nav-orders',
            title: 'Рейсы',
            text: 'Взятые грузы живут здесь: статусы, накладные, расходы по рейсу. ' +
                'Расходы видите только вы — заказчику они не показываются никогда.',
        },
        {
            anchor: 'nav-profile',
            title: 'Документы и статус',
            text: 'Загрузите права, СТС и страховку — с ними берут охотнее. Здесь же ' +
                'переход из физлица в самозанятые: он открывает заявки от юрлиц, ' +
                'а рейтинг и история сделок остаются при вас.',
        },
    ],
    SHIPPER: [
        {
            anchor: 'nav-publish',
            screen: 'publish',
            title: 'Разместите заявку',
            text: 'Маршрут, вес, сроки и кузов. Расстояние считается по дорогам, ' +
                'а рядом со ставкой показывается медиана рынка — чтобы не ставить цену вслепую.',
        },
        {
            anchor: 'mycargos-tabs',
            screen: 'mycargos',
            title: 'Заявки и отклики',
            text: 'Все ваши заявки с предложениями по каждой: разброс цен и лучшее ' +
                'предложение видно сразу, выбирать перевозчика можно не заходя в карточку. ' +
                'Вторая вкладка — ваши отклики, когда вы сами берёте груз.',
        },
        {
            anchor: 'nav-orders',
            title: 'Ведите рейс',
            text: 'После выбора перевозчика заявка становится рейсом: статусы, положение ' +
                'машины на карте, накладные и договор-заявка в PDF.',
        },
        {
            anchor: 'nav-profile',
            title: 'Компания и доверие',
            text: 'Привяжите компанию по ИНН — прямых грузовладельцев перевозчики ' +
                'фильтруют отдельно, и откликов на такие заявки заметно больше.',
        },
    ],
    FORWARDER: [
        {
            anchor: 'nav-feed',
            screen: 'feed',
            title: 'Лента грузов',
            text: 'Свободные заявки приходят живым потоком. Фильтры и сохранённые ' +
                'направления присылают push, когда появляется подходящий груз.',
        },
        {
            anchor: 'publish-route',
            screen: 'publish',
            title: 'И обратная сторона',
            text: 'Взяли груз и ищете под него машину — разместите заявку сами. ' +
                'Маршрут задаёт всё остальное: расстояние считается по дорогам, ' +
                'по нему же подбирается рыночная ставка.',
        },
        {
            anchor: 'nav-trucks',
            title: 'Свободные машины',
            text: 'Доска, где перевозчики объявляют, где освобождаются. Отсюда ' +
                'закрывается обратная загрузка.',
        },
        {
            anchor: 'nav-orders',
            title: 'Рейсы',
            text: 'Сделки в работе: статусы, документы, деньги. Пока заявка свободна — ' +
                'она публичная, как только взята — видна только сторонам.',
        },
        {
            anchor: 'nav-profile',
            title: 'Компания и документы',
            text: 'Привязка компании по ИНН, роли сотрудников и документы. ' +
                'Рейтинг и история сделок переезжают с вами при смене статуса.',
        },
    ],
};
/* ---------- Утилиты ---------- */
/** Расстояние по большому кругу, км. Для оценки, не для тарификации. */
export function haversineKm(aLat, aLng, bLat, bLng) {
    const R = 6371;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(bLat - aLat);
    const dLng = toRad(bLng - aLng);
    const s = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
    return Math.round(2 * R * Math.asin(Math.sqrt(s)));
}
/**
 * Коэффициент извилистости дорог. Прямая линия систематически занижает
 * реальный пробег примерно на четверть — до подключения роутинга
 * используем поправку, чтобы ставка за км не врала.
 */
export const ROAD_FACTOR = 1.27;
export function estimateRoadKm(aLat, aLng, bLat, bLng) {
    return Math.round(haversineKm(aLat, aLng, bLat, bLng) * ROAD_FACTOR);
}
/**
 * Расстояние от точки до отрезка маршрута, км.
 *
 * Считаем в плоскости: на масштабе крюка (десятки километров) кривизна Земли
 * даёт ошибку меньше, чем сама оценка «по прямой вместо дорог». Долгота
 * сжимается по косинусу широты — без этой поправки в Мурманске коридор
 * получается вдвое шире, чем в Сочи.
 */
export function distanceToRouteKm(lat, lng, aLat, aLng, bLat, bLng) {
    const KM_PER_DEG = 111.32;
    const k = Math.cos(((aLat + bLat) / 2) * (Math.PI / 180));
    const px = (lng - aLng) * k * KM_PER_DEG;
    const py = (lat - aLat) * KM_PER_DEG;
    const vx = (bLng - aLng) * k * KM_PER_DEG;
    const vy = (bLat - aLat) * KM_PER_DEG;
    const len2 = vx * vx + vy * vy;
    // Маршрут схлопнулся в точку — считаем расстояние до неё.
    if (len2 === 0)
        return Math.hypot(px, py);
    // Проекция прижимается к концам отрезка: груз за спиной у водителя
    // не «в двух километрах от маршрута», а в двухстах позади старта.
    const t = Math.max(0, Math.min(1, (px * vx + py * vy) / len2));
    return Math.hypot(px - t * vx, py - t * vy);
}
/**
 * Положение точки вдоль маршрута, 0 — старт, 1 — финиш.
 *
 * Нужно, чтобы не предлагать выгрузку раньше погрузки: заявка, у которой
 * точка сдачи стоит до точки забора, «по пути» только на карте.
 */
export function routeProgress(lat, lng, aLat, aLng, bLat, bLng) {
    const KM_PER_DEG = 111.32;
    const k = Math.cos(((aLat + bLat) / 2) * (Math.PI / 180));
    const px = (lng - aLng) * k * KM_PER_DEG;
    const py = (lat - aLat) * KM_PER_DEG;
    const vx = (bLng - aLng) * k * KM_PER_DEG;
    const vy = (bLat - aLat) * KM_PER_DEG;
    const len2 = vx * vx + vy * vy;
    if (len2 === 0)
        return 0;
    return Math.max(0, Math.min(1, (px * vx + py * vy) / len2));
}
/**
 * Насколько ставка отличается от рынка.
 *
 * Сравнение идёт с медианой той выборки, которую человек сейчас видит:
 * поставил фильтр «рефрижератор» — сравнивается с рефрижераторами. Средняя
 * «по всей бирже» вводила бы в заблуждение, потому что трал и газель живут
 * в разных ценовых мирах.
 *
 * Пороги широкие: ±15 % на этом рынке — обычный разброс между заказчиками,
 * а не сигнал. Красный оставлен на случай, когда ставка вдвое ниже рынка:
 * такие заявки обычно висят неделями, и предупредить о них честно.
 */
export const RATE_TONES = ['high', 'mid', 'low', 'poor'];
export const RATE_TONE_HINT = {
    high: 'Выше рынка по этой выборке',
    mid: 'В рынке',
    low: 'Ниже рынка',
    poor: 'Сильно ниже рынка — откликов может не быть',
};
export function rateTone(perKm, medianPerKm) {
    if (!perKm || !medianPerKm)
        return null;
    const ratio = perKm / medianPerKm;
    if (ratio >= 1.15)
        return 'high';
    if (ratio >= 0.85)
        return 'mid';
    if (ratio >= 0.6)
        return 'low';
    return 'poor';
}
/** Медиана. Не среднее: одна заявка с опечаткой в цене сдвигает среднее. */
export function medianOf(values) {
    const list = values.filter((v) => v > 0).sort((a, b) => a - b);
    if (list.length === 0)
        return null;
    const mid = Math.floor(list.length / 2);
    return list.length % 2 ? list[mid] : Math.round((list[mid - 1] + list[mid]) / 2);
}
export function formatRub(v) {
    return new Intl.NumberFormat('ru-RU').format(v) + ' ₽';
}
export * from './trucks.js';
